// src/App.js
import React, { useState, useEffect, useRef, useCallback, useMemo} from 'react';
import GameBoard from './GameBoard';
import HistoryPanel from './HistoryPanel';
import LogPanel from './LogPanel';
import Header from './Header';
import GameOverModal from './GameOverModal';
import { LLM_PROVIDERS, loadApiKeys } from './llmProviders';
import PauseMenu from './PauseMenu';
import VisualizationModeSelector from './VisualizationModeSelector';
import HeatmapOverlay from './HeatmapOverlay';
import './App.css';
import { TILE_TYPE } from './utils';
import CoordinateSystemIcon from './CoordinateSystemIcon';
import PhutballEngine from './PhutballEngine';
import { usePuzzleQueue } from './hooks/usePuzzleQueue';
import { fetchPuzzleFromCatalog } from './puzzleCatalog';
import { formatBoardCoordinate } from './coordinateFormatting';
import { saveSession } from './savedSession';
import { requestLLMMove } from './llmService';
import { trackEvent } from './telemetry';
import AlphaZeroService from './alphaZeroService';

const LOCAL_GAME_KEY = 'phutball_local_game';

const saveLocalGame = (moves, config) => {
    try {
        localStorage.setItem(LOCAL_GAME_KEY, JSON.stringify({
            moves,
            rows: config?.rows ?? 21,
            cols: config?.cols ?? 15,
            savedAt: Date.now(),
        }));
    } catch (e) { /* quota */ }
};

const loadLocalGame = (config) => {
    try {
        const raw = localStorage.getItem(LOCAL_GAME_KEY);
        if (!raw) return null;
        const saved = JSON.parse(raw);
        // Must match current config dimensions
        if (saved.rows !== (config?.rows ?? 21) || saved.cols !== (config?.cols ?? 15)) return null;
        if (!Array.isArray(saved.moves) || saved.moves.length === 0) return null;
        return saved;
    } catch (e) { return null; }
};

export const clearLocalGame = () => {
    try { localStorage.removeItem(LOCAL_GAME_KEY); } catch (e) { /* ignore */ }
};

const emptyJumpPreview = {
    jumpedPieces: [],
    usageCounts: {},
    invalidIndex: -1,
    isValid: false,
};

const cloneBoard = (board) => board?.map?.(row => row.slice()) ?? [];

const posKey = (r, c) => `${r},${c}`;

const positionsEqual = (a, b) => {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
        if (a[i] !== b[i]) return false;
    }
    return true;
};

const extractEntryPlayer = (entry) => {
    if (typeof entry !== 'string') return null;
    const match = entry.match(/P\s*(\d)/i);
    if (!match) return null;
    const value = Number(match[1]);
    return Number.isFinite(value) ? value : null;
};

const computeTurnStarts = (historyEntries) => {
    if (!Array.isArray(historyEntries)) return [];
    const starts = [];
    let lastPlayer = null;
    historyEntries.forEach((entry, idx) => {
        const player = extractEntryPlayer(entry);
        if (player == null) {
            return;
        }
        if (player !== lastPlayer) {
            starts.push(idx);
            lastPlayer = player;
        }
    });
    return starts;
};

const advanceToTurn = (historyEntries, turnStarts, currentIndex, direction) => {
    const entries = Array.isArray(historyEntries) ? historyEntries : [];
    const len = entries.length;
    if (!len) return 0;
    const boundedDirection = direction >= 0 ? 1 : -1;
    const safeIndex = Number.isFinite(currentIndex) ? currentIndex : 0;
    if (!Array.isArray(turnStarts) || turnStarts.length === 0) {
        const fallback = Math.max(0, Math.min(safeIndex + boundedDirection, len - 1));
        return Number.isNaN(fallback) ? 0 : fallback;
    }

    const boundedIndex = Math.max(0, Math.min(safeIndex, len - 1));
    let currentBoundaryIdx = -1;
    for (let i = 0; i < turnStarts.length; i += 1) {
        if (turnStarts[i] <= boundedIndex) {
            currentBoundaryIdx = i;
        } else {
            break;
        }
    }

    let result;
    if (boundedDirection > 0) {
        if (currentBoundaryIdx === -1) {
            result = turnStarts[0];
        } else if (currentBoundaryIdx + 1 < turnStarts.length) {
            result = turnStarts[currentBoundaryIdx + 1];
        } else {
            result = len - 1;
        }
    } else if (currentBoundaryIdx <= 0) {
        result = 0;
    } else {
        result = turnStarts[currentBoundaryIdx - 1];
    }

    if (result === boundedIndex || result == null || Number.isNaN(result)) {
        const fallback = Math.max(0, Math.min(boundedIndex + boundedDirection, len - 1));
        return Number.isNaN(fallback) ? boundedIndex : fallback;
    }

    return result;
};

const HALT_REGEX = /\bHALT\b/i;

const parsePlacementEntry = (entry) => {
    if (typeof entry !== 'string') return null;
    const placementMatch = entry.match(/Place at\s*\((\d+),\s*(\d+)\)/);
    if (!placementMatch) {
        return null;
    }
    return {
        type: 'placement',
        position: [Number(placementMatch[1]), Number(placementMatch[2])],
    };
};

const parseJumpEntry = (entry) => {
    if (typeof entry !== 'string' || !entry.includes('Jump')) {
        return null;
    }
    const coordMatches = [...entry.matchAll(/\((\d+),\s*(\d+)\)/g)]
        .map(match => [Number(match[1]), Number(match[2])]);
    if (coordMatches.length < 2) {
        return null;
    }
    return {
        type: 'jump',
        start: coordMatches[0],
        path: coordMatches.slice(1),
    };
};

const isHaltEntry = (entry) => typeof entry === 'string' && HALT_REGEX.test(entry);

const aggregateJumpSequenceBefore = (historyEntries, haltIndex) => {
    if (!Array.isArray(historyEntries)) return null;
    if (typeof haltIndex !== 'number' || haltIndex <= 0 || haltIndex >= historyEntries.length) {
        return null;
    }
    const haltEntry = historyEntries[haltIndex];
    if (!isHaltEntry(haltEntry)) {
        return null;
    }

    const haltPlayer = extractEntryPlayer(haltEntry);
    const segments = [];
    let requiredLanding = null;

    for (let idx = haltIndex - 1; idx >= 0; idx -= 1) {
        const candidate = historyEntries[idx];
        if (typeof candidate !== 'string' || !candidate.includes('Jump')) {
            break;
        }
        if (haltPlayer != null) {
            const candidatePlayer = extractEntryPlayer(candidate);
            if (candidatePlayer != null && candidatePlayer !== haltPlayer) {
                break;
            }
        }
        const parsedJump = parseJumpEntry(candidate);
        if (!parsedJump || !Array.isArray(parsedJump.path) || !parsedJump.path.length) {
            break;
        }
        const landing = parsedJump.path[parsedJump.path.length - 1];
        if (requiredLanding && (!landing || !positionsEqual(landing, requiredLanding))) {
            break;
        }

        segments.unshift(parsedJump);
        requiredLanding = parsedJump.start;
    }

    if (!segments.length) {
        return null;
    }

    const aggregatedStart = segments[0].start;
    const aggregatedPath = segments.reduce((acc, segment) => acc.concat(segment.path), []);

    if (!aggregatedStart || !aggregatedPath.length) {
        return null;
    }

    return {
        type: 'jump',
        start: aggregatedStart,
        path: aggregatedPath,
    };
};

const PANEL_CONFIG = [
    { id: 'log', label: 'Chat & Log', shortLabel: 'Chat' },
    { id: 'board', label: 'Game Board', shortLabel: 'Board' },
    { id: 'history', label: 'History', shortLabel: 'History' },
];

const clampPanelIndex = (value) => Math.max(0, Math.min(PANEL_CONFIG.length - 1, value));

const shouldUseStackedLayout = () => {
    if (typeof window === 'undefined') {
        return false;
    }
    const { innerWidth, innerHeight } = window;
    const width = typeof innerWidth === 'number' ? innerWidth : window.screen?.width ?? 0;
    const height = typeof innerHeight === 'number' ? innerHeight : window.screen?.height ?? 0;
    if (!width || !height) {
        return width <= 1200;
    }
    const portrait = height > width;
    return width <= 1200 || portrait;
};

const POINTER_SWIPE_FRACTION = 0.3;
const WHEEL_SWIPE_FRACTION = 0.5;
const MIN_SWIPE_DISTANCE = 120;
const WHEEL_COOLDOWN_MS = 420;

const computeSwipeThreshold = (element, fraction = POINTER_SWIPE_FRACTION) => {
    const width = element?.clientWidth
        ?? window.innerWidth
        ?? document.documentElement?.clientWidth
        ?? 0;
    const fractionTarget = width > 0 ? width * fraction : 0;
    if (fractionTarget > 0) {
        return Math.max(MIN_SWIPE_DISTANCE, fractionTarget);
    }
    return MIN_SWIPE_DISTANCE;
};

const logSwipeDebug = (...args) => {
    if (process.env.NODE_ENV !== 'production') {
        console.log('[panel-swipe]', ...args);
    }
};

const parseHistoryEntryForPreview = (entry, options = {}) => {
    if (typeof entry !== 'string') return null;

    const placement = parsePlacementEntry(entry);
    if (placement) {
        return placement;
    }

    const jump = parseJumpEntry(entry);
    if (jump) {
        return jump;
    }

    if (isHaltEntry(entry)) {
        const aggregated = aggregateJumpSequenceBefore(options.history, options.index);
        if (aggregated) {
            return aggregated;
        }
        return null;
    }

    return null;
};

const enumerateIntermediateSquares = (start, end) => {
    if (!Array.isArray(start) || !Array.isArray(end)) return [];
    const [r0, c0] = start;
    const [r1, c1] = end;
    const dr = r1 - r0;
    const dc = c1 - c0;
    const steps = Math.max(Math.abs(dr), Math.abs(dc));
    if (steps <= 1) return [];
    const rStep = Math.sign(dr);
    const cStep = Math.sign(dc);
    const jumped = [];
    for (let i = 1; i < steps; i += 1) {
        jumped.push([r0 + i * rStep, c0 + i * cStep]);
    }
    return jumped;
};

// Single-turn puzzle evaluator. Called after the responder commits a
// move; returns { solved: bool, label: string } describing the verdict
// shown in the GameOverModal. Each puzzle type has its own success
// condition.
const evaluatePuzzleAfterMove = (engine, meta, movePayload) => {
    if (!meta) return { solved: false, label: 'Puzzle complete' };
    const t = meta.puzzle_type || 'win';

    if (t === 'win') {
        // Win puzzle is solved iff the engine ended with the puzzle's
        // player as winner. Any other outcome (no win, opponent win)
        // means the responder didn't execute the canonical solution.
        const won = engine.winner === meta.player;
        return {
            solved: won,
            label: won ? 'Puzzle solved' : 'Did not score this turn',
        };
    }

    if (t === 'threat') {
        // Threat puzzle is solved iff the player placed a stone AND
        // replaying the opponent's canonical chain from the post-move
        // state fails to legally terminate as opp-win. This is the
        // "loose" disrupted_threat metric — any disruption counts.
        if (movePayload?.type !== 'placement') {
            return { solved: false, label: 'Threat puzzle requires a placement' };
        }
        try {
            const replay = new PhutballEngine(engine.rows, engine.cols);
            replay.loadSnapshot(engine.getSnapshot());
            replay.currentPlayer = meta.threat_player;
            const chain = meta.chain_landings || [];
            let allLegal = true;
            for (const landing of chain) {
                const r = replay.applyJump(landing);
                if (!r.success) { allLegal = false; break; }
            }
            // applyJump no longer auto-terminates on an endzone landing (only
            // the final committed landing decides the winner, per the
            // passthrough rule). So we must explicitly halt the sequence to
            // run the win check; without this, oppWon stayed false for every
            // chain and the threat read as "disrupted" no matter what stone
            // the responder placed.
            let oppWon = false;
            if (allLegal) {
                replay.applyHalt();
                oppWon = (replay.winner === meta.threat_player);
            }
            const disrupted = !(allLegal && oppWon);
            // Strict secondary metric: was the placement on a canonical pivot?
            const placedAt = movePayload.path?.[0];
            const onPivot = !!placedAt && (meta.canonical_pivots || []).some(
                ([r, c]) => r === placedAt[0] && c === placedAt[1]
            );
            return {
                solved: disrupted,
                label: disrupted
                    ? (onPivot ? 'Threat disrupted at canonical pivot' : 'Threat disrupted')
                    : 'Threat survives — opponent still wins next turn',
            };
        } catch (err) {
            console.error('[Puzzle] threat eval failed:', err);
            return { solved: false, label: 'Eval failed' };
        }
    }

    if (t === 'passthrough') {
        // Passthrough: legal jump sequence with at least one INTERMEDIATE
        // landing in the player's own (defensive) endzone, final landing
        // closer to opponent's endzone than the start, AND final landing
        // not in player's own endzone (not a self-loss).
        if (movePayload?.type !== 'jump' || !Array.isArray(movePayload.path) || movePayload.path.length < 2) {
            return { solved: false, label: 'Passthrough requires a multi-jump sequence' };
        }
        const path = movePayload.path;
        const ownEz = meta.own_endzone_rows || [];
        const startPos = meta.start_pos || [-1, -1];
        const finalPos = path[path.length - 1];
        const intermediates = path.slice(0, -1);

        const passedThrough = intermediates.some(([r]) => ownEz.includes(r));
        // P1 attacks toward row 0 (north), P2 toward row rows-1 (south).
        const player = meta.player;
        const progress = (player === 1)
            ? (startPos[0] - finalPos[0])   // positive = north for P1
            : (finalPos[0] - startPos[0]);  // positive = south for P2
        const finalInOwnEz = ownEz.includes(finalPos[0]);
        // engine.winner == 3 - player would mean opponent won; for passthrough
        // a self-loss is the typical failure mode (final landing in own ez).
        const selfLost = engine.winner === (3 - player);

        const success = passedThrough && progress > 0 && !finalInOwnEz && !selfLost;
        let label;
        if (success) {
            const won = engine.winner === player;
            label = won
                ? `Passthrough advance scored — ${progress} rows past start, landed in opponent endzone`
                : `Passthrough advance — ${progress} rows closer to opponent endzone`;
        } else if (selfLost || finalInOwnEz) {
            label = 'Passthrough but landed in own endzone (self-loss)';
        } else if (!passedThrough) {
            label = 'Did not pass through own endzone';
        } else if (progress <= 0) {
            label = 'Passed through but no net progress toward opponent';
        } else {
            label = 'Passthrough sequence rejected';
        }
        return { solved: success, label };
    }

    if (t === 'denial') {
        // Denial: a single multi-jump turn must (a) consume the canonical
        // loop's stones, (b) advance forward, (c) loop landings precede
        // advance landings in the played sequence. Strategic correctness
        // = conjunction. Report all three booleans.
        if (movePayload?.type !== 'jump' || !Array.isArray(movePayload.path) || movePayload.path.length < 2) {
            return { solved: false, label: 'Denial requires a multi-jump sequence' };
        }
        const path = movePayload.path;
        const player = meta.player;
        const startPos = meta.start_pos || [-1, -1];
        const loopLandings = meta.loop_landings || [];
        const advLandings = meta.advance_landings || [];
        const oppEz = meta.opp_endzone_rows || [];

        // Find the index in the played path where the ball returns to start
        // (or near start). All canonical loop landings should appear at or
        // before that index; advance landings should appear after.
        const eq = (a, b) => a[0] === b[0] && a[1] === b[1];
        const returnIdx = path.findIndex(p => eq(p, startPos));
        const playedSet = new Set(path.map(p => `${p[0]},${p[1]}`));

        // (a) loop_consumed: every canonical loop landing appears in the
        // played path.
        const loopConsumed = loopLandings.every(p => playedSet.has(`${p[0]},${p[1]}`));

        // (b) advanced: every canonical advance landing appears.
        const advanced = advLandings.every(p => playedSet.has(`${p[0]},${p[1]}`));

        // (c) correct_order: ball returns to start at some intermediate
        // index, all loop landings come at index ≤ returnIdx, all advance
        // landings come at index > returnIdx.
        let correctOrder = false;
        if (returnIdx >= 0) {
            const loopIdxs = loopLandings.map(p => path.findIndex(q => eq(q, p)));
            const advIdxs = advLandings.map(p => path.findIndex(q => eq(q, p)));
            const allLoopBefore = loopIdxs.every(i => i >= 0 && i <= returnIdx);
            const allAdvAfter = advIdxs.every(i => i > returnIdx);
            correctOrder = allLoopBefore && allAdvAfter;
        }

        // Final position must NOT be in either endzone.
        const finalPos = path[path.length - 1];
        const finalRow = finalPos[0];
        const ownEz = meta.own_endzone_rows || [];
        const finalInEndzone = ownEz.includes(finalRow) || oppEz.includes(finalRow);

        const success = loopConsumed && advanced && correctOrder && !finalInEndzone;
        let label;
        if (success) {
            label = 'Denial solved — material consumed and advance executed';
        } else if (!loopConsumed) {
            label = 'Skipped the consuming loop — opponent material untouched';
        } else if (!advanced) {
            label = 'Consumed material but did not advance toward opponent endzone';
        } else if (!correctOrder) {
            label = 'Loop and advance played out of canonical order';
        } else if (finalInEndzone) {
            label = 'Final landing in an endzone — terminal turn';
        } else {
            label = 'Denial sequence rejected';
        }
        return { solved: success, label };
    }

    // Unknown puzzle type fallback.
    const won = engine.winner === meta.player;
    return {
        solved: won,
        label: won ? 'Puzzle solved' : 'Did not score this turn',
    };
};

const buildPreviewFromJumpPath = (start, path) => {
    if (!Array.isArray(start) || !Array.isArray(path) || path.length === 0) {
        return emptyJumpPreview;
    }

    const usageCounts = {};
    usageCounts[posKey(start[0], start[1])] = 1;
    const jumpedPieces = [];
    let current = start;
    path.forEach(target => {
        if (!Array.isArray(target)) return;
        const key = posKey(target[0], target[1]);
        usageCounts[key] = (usageCounts[key] ?? 0) + 1;
        jumpedPieces.push(...enumerateIntermediateSquares(current, target));
        current = target;
    });

    const uniqueJumped = [];
    const seen = new Set();
    jumpedPieces.forEach(([r, c]) => {
        const key = posKey(r, c);
        if (!seen.has(key)) {
            uniqueJumped.push([r, c]);
            seen.add(key);
        }
    });

    return {
        jumpedPieces: uniqueJumped,
        usageCounts,
        invalidIndex: -1,
        isValid: true,
    };
};

const validateJumpSegment = (board, start, end) => {
    if (!Array.isArray(start) || !Array.isArray(end)) {
        return { valid: false, jumped: [] };
    }

    const [sr, sc] = start;
    const [er, ec] = end;
    const dr = er - sr;
    const dc = ec - sc;

    const isAligned = (dr === 0 || dc === 0 || Math.abs(dr) === Math.abs(dc));
    if (!isAligned) {
        return { valid: false, jumped: [] };
    }

    const rStep = Math.sign(dr);
    const cStep = Math.sign(dc);
    if (rStep === 0 && cStep === 0) {
        return { valid: false, jumped: [] };
    }

    let r = sr + rStep;
    let c = sc + cStep;
    const jumped = [];
    let encountered = 0;

    while (!(r === er && c === ec)) {
        const tile = board?.[r]?.[c];
        if (tile !== TILE_TYPE.PHUTBALL_MAN) {
            return { valid: false, jumped: [] };
        }
        jumped.push([r, c]);
        encountered += 1;
        r += rStep;
        c += cStep;
    }

    const landingTile = board?.[er]?.[ec];
    // Must have jumped over at least one man
    // Landing tile can be EMPTY or an endzone tile (END_HI/END_LO for scoring jumps)
    const isValidLanding = landingTile === TILE_TYPE.EMPTY ||
                           landingTile === TILE_TYPE.END_HI ||
                           landingTile === TILE_TYPE.END_LO;
    if (encountered === 0 || landingTile === undefined || !isValidLanding) {
        return { valid: false, jumped: [] };
    }

    return { valid: true, jumped };
};

const evaluateJumpSequence = (board, startPos, path) => {
    if (!Array.isArray(board) || board.length === 0 || !Array.isArray(startPos)) {
        return emptyJumpPreview;
    }

    const boardCopy = cloneBoard(board);
    const usageCounts = {};
    const startKey = `${startPos[0]},${startPos[1]}`;
    usageCounts[startKey] = 1;

    let invalidIndex = -1;
    let current = startPos;
    let isValid = true;
    const jumpedPieces = [];

    path.forEach((target, idx) => {
        if (!Array.isArray(target)) {
            if (invalidIndex === -1) {
                invalidIndex = idx;
                isValid = false;
            }
            return;
        }

        if (invalidIndex === -1) {
            const { valid, jumped } = validateJumpSegment(boardCopy, current, target);
            if (valid) {
                jumpedPieces.push(...jumped);
                const [cr, cc] = current;
                boardCopy[cr][cc] = TILE_TYPE.EMPTY;
                jumped.forEach(([jr, jc]) => {
                    boardCopy[jr][jc] = TILE_TYPE.EMPTY;
                });
                const [tr, tc] = target;
                boardCopy[tr][tc] = TILE_TYPE.PHUTBALL;
            } else {
                invalidIndex = idx;
                isValid = false;
            }
        }

        const key = `${target[0]},${target[1]}`;
        usageCounts[key] = (usageCounts[key] ?? 0) + 1;
        current = target;
    });

    if (path.length > 0 && invalidIndex === -1) {
        isValid = true;
    }

    return {
        jumpedPieces,
        usageCounts,
        invalidIndex,
        isValid,
    };
};

const isLegalPlacement = (board, pos) => {
    if (!Array.isArray(board) || board.length === 0 || !Array.isArray(pos)) {
        return false;
    }
    const [r, c] = pos;
    // Endzone rows (first and last) are forbidden for placement
    if (r <= 0 || r >= board.length - 1) return false;
    const tile = board?.[r]?.[c];
    return tile === TILE_TYPE.EMPTY;
};

// ── Local game tree helpers (matches server's simplified_move_string + HistoryNode.to_dict) ──

const simplifiedMoveString = (moveDesc) => {
    if (!moveDesc || moveDesc === 'Game Start') return 'start';
    if (moveDesc.includes('Place at')) {
        const player = moveDesc.split(':')[0].trim();
        const position = moveDesc.split('Place at')[1].trim();
        return `${player}-place-${position}`;
    }
    if (moveDesc.includes('Jump')) {
        const player = moveDesc.split(':')[0].trim();
        if (moveDesc.includes('Halt jump sequence')) return `${player}-halt`;
        const parts = moveDesc.split('Jump')[1].trim().split('->').map(s => s.trim());
        if (parts.length >= 2) return `${player}-jump-${parts[0]}-${parts[parts.length - 1]}`;
        return `${player}-jump`;
    }
    return moveDesc.slice(0, 10);
};

const createTreeNode = (uid, moveDesc, player, statePlayer, parentId, isJump, isHalt, turn, branchId) => ({
    id: `node-${uid}`,
    moveDesc,
    moveSummary: simplifiedMoveString(moveDesc),
    player,
    statePlayer,
    isJump,
    isHalt,
    turn,
    parentId,
    children: [],
    branchId,
    uid,
});

const isPlainObject = (value) => value && typeof value === 'object';

const areMovesEqual = (a, b) => {
    if (a === b) return true;
    if (!isPlainObject(a) || !isPlainObject(b)) return false;
    return JSON.stringify(a) === JSON.stringify(b);
};

const arePreviewsEqual = (a, b) => {
    if (a === b) return true;
    if (!isPlainObject(a) || !isPlainObject(b)) return false;
    return JSON.stringify(a) === JSON.stringify(b);
};

const App = ({ config, onExit, restoreSessionData }) => {
    // Extract board dimensions from config, with defaults
    const configRows = config?.rows ?? 21;
    const configCols = config?.cols ?? 15;
    const initialPhutballPos = [Math.floor(configRows / 2), Math.floor(configCols / 2)];

    // Any game that doesn't need the server backend runs locally
    const needsServer = false; // AlphaZero now runs client-side; LLMs also run client-side
    const isLocalGame = !needsServer;
    const engineRef = useRef(null);
    const localHistoryRef = useRef([]);     // history entry strings
    const localLogHistoryRef = useRef([]);  // structured log entries
    const localSnapshotsRef = useRef([]);   // engine snapshots for undo
    const localTreeRootRef = useRef(null);  // history tree root node
    const localTreeCurrentRef = useRef(null); // current (live) tree node
    const localTreeUidRef = useRef(0);      // uid counter for tree nodes
    const localNodeSnapshotsRef = useRef(new Map()); // nodeId -> engine snapshot
    const localStatsHistoryRef = useRef([]);  // per-move stats entries for StatsPlot
    const localMovesRef = useRef([]);  // move payloads for persistence
    const gameStartAtRef = useRef(null);  // timestamp for game_complete duration
    const gameTokenTotalsRef = useRef(null);  // { input, output, reasoning, cached, costUsd } accumulator
    const gameCompleteSentRef = useRef(false);  // dedupe game_complete beacon

    const socketRef = useRef(null);
    const sessionRestoreSent = useRef(false);
    const pendingMoveRef = useRef(null);
    const pendingTreeSelectionRef = useRef(null);
    const previewedTreeEdgeRef = useRef({ parentId: null, childId: null });
    const [previewedTreeEdge, setPreviewedTreeEdge] = useState({ parentId: null, childId: null });
    const toggleJumpEntryRef = useRef(null);
    const clearStageOnNextStateRef = useRef(false);
    const isReplayingRef = useRef(false);
    const wasReplayingRef = useRef(false);
    const historyHighlightRef = useRef({ should: false, index: null });
    const [historyHighlightToken, setHistoryHighlightToken] = useState(0);
    const [awaitingAck, setAwaitingAck] = useState(false);
    const selectedHistoryIndexRef = useRef(null);
    const pendingLiveFocusRef = useRef(false);
    const enteringTurnModeRef = useRef(false);  // Track when entering turn mode to preserve perspective
    const historyHoverPreviewRef = useRef({ active: false, index: null, source: null });
    const previewRequestRef = useRef({ source: null, historyIndex: null, parentNodeId: null, nodeId: null });
    const hoverTimeoutRef = useRef(null);
    const skipFirstEffectRef = useRef(process.env.NODE_ENV === 'development');
    const [gameState, setGameState] = useState({
        board: [], currentPlayer: 1, winner: 0, history: [], historyIndex: 0, phutballPos: initialPhutballPos, historyTree: null, pieceTilt: null, statsHistory: [], logHistory: [], initialBoardText: null
    });
    const latestStateRef = useRef({
        board: [], currentPlayer: 1, history: [], historyIndex: 0, phutballPos: initialPhutballPos
    });
    const [coordSystem, setCoordSystem] = useState('endzone');
    const [showMinimap, setShowMinimap] = useState(true);
    const [historyView, setHistoryView] = useState('linear');
    const [status, setStatus] = useState("Connecting...");
    const [isReplayingHistory, setIsReplayingHistory] = useState(false);
    const [lockCameraDuringReplay, setLockCameraDuringReplay] = useState(true);
    const [keyFocus, setKeyFocus] = useState('board');
    const [historyFocusIndex, setHistoryFocusIndex] = useState(0);
    const [previewPerspectivePlayer, setPreviewPerspectivePlayer] = useState(null);
    const [treePreviewActive, setTreePreviewActive] = useState(false);
    const [historyPreviewState, setHistoryPreviewState] = useState(null);
    const [historyBranchMode, setHistoryBranchMode] = useState(false);
    const [isInTurnMode, setIsInTurnMode] = useState(false); // true when at live node with next player's perspective
    const [isPauseMenuOpen, setIsPauseMenuOpen] = useState(false);
    // Menu opener used by the hamburger and the Escape key. Once a puzzle
    // attempt has been judged, the menu is the puzzle-complete modal (verdict,
    // attempt stats, model switcher, Retry / New Puzzle), not the pause menu:
    // the pause menu's "New Puzzle" gives no hint whether a model or a human
    // plays next. A ref lets the keyboard effect call the latest version
    // without adding it to that effect's dependency list.
    const openGameMenuRef = useRef(() => {});
    const [dismissedGameOverNodeId, setDismissedGameOverNodeId] = useState(null);  // Track which node's game over was dismissed
    const controlsRef = useRef(null);
    const [controlsReady, setControlsReady] = useState(false);

    // NN Visualization state
    const [visualizationNodeId, setVisualizationNodeId] = useState(null);
    const [visualizationMode, setVisualizationMode] = useState('off'); // 'off' | 'placement-policy' | 'jump-policy' | 'saliency-0' through 'saliency-5'
    const [showValueEstimate, setShowValueEstimate] = useState(false);
    const [nnData, setNnData] = useState(null); // Will hold { policyProbs, valueEstimate, saliencyMap }
    const [showModeSelectorForNode, setShowModeSelectorForNode] = useState(null); // null or nodeId
    const [nnDataStatus, setNnDataStatus] = useState(new Map()); // Map<nodeId, "Pending" | "Processing" | "Failed" | "Complete">

    // LLM/AI state
    const [llmThinking, setLlmThinking] = useState(false);
    const [llmError, setLlmError] = useState(null);
    const [puzzleMetadata, setPuzzleMetadata] = useState(null);
    // Live puzzle dial values; initialized from config.puzzle and editable
    // from the GameOverModal's "New Puzzle" controls so the next puzzle
    // can be reshaped without leaving the game screen.
    const [puzzleControls, setPuzzleControls] = useState(() => ({
        puzzle_type: config?.puzzle?.puzzle_type ?? 'win',
        num_jumps: config?.puzzle?.num_jumps ?? 1,
        noise: config?.puzzle?.noise ?? 10,
        max_jump_len: config?.puzzle?.max_jump_len ?? 3,
        player: config?.puzzle?.player ?? 1,
    }));
    // Puzzle single-turn evaluation: every puzzle is one turn; once the
    // responder commits a move we freeze the game and show pass/fail in
    // the modal. null = puzzle still in progress; { solved, label } once
    // it has been judged. Cleared when a new puzzle is fetched.
    const [puzzleEvalResult, setPuzzleEvalResult] = useState(null);
    // Set synchronously when a puzzle attempt is committed, before the hop
    // animation and the deferred evaluator publish their state. The LLM
    // effect guards on it so it cannot re-query the model in that window.
    const puzzleAttemptCommittedRef = useRef(false);
    // Watch-an-LLM puzzles: the model, timing and token usage of the one
    // committed attempt, shown in the puzzle-complete modal. Reset per puzzle.
    const [puzzleLLMStats, setPuzzleLLMStats] = useState(null);
    // The exact catalog entry currently loaded, so "Retry" can reload the
    // same position (the catalog picks by seed modulo pool size, so a refetch
    // by seed is not guaranteed to return the same entry).
    const lastPuzzleDataRef = useRef(null);
    // For "Watch an LLM" puzzle mode: auto-populated from config.llmProvider /
    // llmModel / llmApiKey (set on the PuzzleConfig screen). Drives the patches
    // to isCurrentPlayerAI / getCurrentAIConfig below so the existing AI-vs-Human
    // requestLLMMove flow handles the puzzle turn without any extra runtime UI.
    const [puzzleAIOverride, setPuzzleAIOverride] = useState(() => {
        if (config?.puzzleAttemptMode === 'llm' && config?.llmProvider && config?.llmModel && config?.llmApiKey) {
            return { provider: config.llmProvider, model: config.llmModel, apiKey: config.llmApiKey };
        }
        return null;
    });
    // Mirror metadata in a ref so the eval helper called from processLocalMove
    // (a useCallback with stable deps) sees the latest server response.
    const puzzleMetadataRef = useRef(null);
    useEffect(() => { puzzleMetadataRef.current = puzzleMetadata; }, [puzzleMetadata]);

    // ── Endless puzzle mode ────────────────────────────────────────────
    // When the user picked "Endless puzzles" the runtime pulls pre-generated
    // puzzles from usePuzzleQueue between attempts. The queue keeps fast
    // types (win/passthrough/denial) full near-instantly and prioritises
    // background generation of slow threat puzzles. First mistake ends the
    // run and triggers a summary modal.
    const isEndlessMode = config?.puzzleAttemptMode === 'endless';
    const endlessApiBase = (process.env.REACT_APP_API_URL ||
        (typeof window !== 'undefined'
            ? `${window.location.protocol}//${window.location.hostname}:8000`
            : 'http://localhost:8000'));
    const endlessEnabledTypes = config?.endless?.enabledTypes || [];
    const endlessPerTypeConfig = config?.endless?.perTypeConfig || {};
    const puzzleQueue = usePuzzleQueue({
        enabled: isEndlessMode,
        apiBase: endlessApiBase,
        rows: config?.rows ?? 21,
        cols: config?.cols ?? 15,
        perTypeConfig: endlessPerTypeConfig,
        enabledTypes: endlessEnabledTypes,
    });
    // Per-run stats accumulator: how many of each type the user solved
    // and how long they took. Reset on each new endless run.
    const endlessStatsRef = useRef(null);
    if (endlessStatsRef.current === null && isEndlessMode) {
        endlessStatsRef.current = {
            startedAt: Date.now(),
            byType: { win: [], threat: [], passthrough: [], denial: [] }, // [solveTimeMs, ...]
            currentPuzzleStartedAt: null,
            currentPuzzleType: null,
        };
    }
    // Set when the run ends (first mistake). Drives the summary modal.
    const [endlessRunSummary, setEndlessRunSummary] = useState(null);
    // True when we're ready to pop the next puzzle from the queue (initial
    // mount, and after each correct solve). The "ready" effect below loads
    // it into the engine once the queue has something to serve.
    const [endlessNeedsNext, setEndlessNeedsNext] = useState(isEndlessMode);
    // Generation counter for the multi-hop jump animation. processLocalMove
    // increments it on each new animation start; tick() captures the value
    // and bails if the current ref no longer matches (i.e., someone called
    // handleNewGame / loaded a new puzzle while the animation was running).
    // Without this, pending setTimeouts continue firing setGameState updates
    // against a board that has since been replaced, which looks like the
    // previous jump "replaying" on top of the new puzzle.
    const animGenRef = useRef(0);
    // Set true while a /puzzle fetch is in flight; second clicks on
    // 'New Puzzle' (modal or pause menu) become no-ops until it resolves.
    // Threat puzzles can take 4–7 s to generate, which is long enough to
    // race-click and queue up multiple cascading regens otherwise.
    const puzzleFetchInFlightRef = useRef(false);
    // Mirrors the ref as state so the board can render a 'Generating puzzle…'
    // overlay during the wait (refs don't trigger re-renders on their own).
    const [puzzleFetching, setPuzzleFetching] = useState(false);
    const llmRequestPending = useRef(false);

    // Client-side AlphaZero
    const azServiceRef = useRef(null);
    // Win-probability line: a second worker evaluates each position with the value head (the move worker handles one
    // request at a time, so evaluations must not share it). Queued so evaluations never overlap.
    const valueSvcRef = useRef(null);
    const valueQueueRef = useRef(Promise.resolve());
    const queueValueEval = useCallback((entry, snap) => {
        if (snap.winner) { entry.value = snap.winner === 1 ? -1 : 1; return; }
        if (!valueSvcRef.current?.isReady) { entry.pendingSnap = snap; return; }   // evaluated once the worker is up
        valueQueueRef.current = valueQueueRef.current.then(async () => {
            const svc = valueSvcRef.current;
            if (!svc?.isReady) return;
            const r = await svc.quickEval({ board: snap.board, phutballPos: snap.phutballPos, currentPlayer: snap.currentPlayer,
                isJumping: (snap.jumpSeq || []).length > 0, jumpSeq: snap.jumpSeq || [], jumpVisitCounts: snap.jumpVisitCounts || {},
                winner: 0 });
            // value head: side to move; chart: -1 = P1 winning, +1 = P2 winning
            entry.value = snap.currentPlayer === 1 ? -r.value : r.value;
            setGameState(prev => prev ? { ...prev, statsHistory: [...localStatsHistoryRef.current] } : prev);
        }).catch(err => console.warn('[App] value eval failed:', err));
    }, []);
    const [azLoading, setAzLoading] = useState(false);

    const [swipeOffsetPct, setSwipeOffsetPct] = useState(0);
    const [isPagerDragging, setIsPagerDragging] = useState(false);
    const [forceNoTransition, setForceNoTransition] = useState(false);
    const wheelGestureStateRef = useRef({ accumX: 0, lockedUntil: 0 });
    const settlePagerOffset = useCallback(() => {
        setIsPagerDragging(false);
        setForceNoTransition(true);
        setSwipeOffsetPct(0);
    }, []);

    useEffect(() => {
        if (!forceNoTransition) {
            return undefined;
        }
        const id = requestAnimationFrame(() => setForceNoTransition(false));
        return () => cancelAnimationFrame(id);
    }, [forceNoTransition]);
    const registerBoardControls = (controls) => {
        controlsRef.current = controls;
        setControlsReady(true);
      };

    const registerToggleJumpEntry = useCallback((handler) => {
        toggleJumpEntryRef.current = handler;
    }, []);

    const [hoverPos, setHoverPos] = useState(null);
    const [cursorPos, setCursorPos] = useState(null);
    const cursorPosRef = useRef(null);
    const handleHoverCell = (r, c) => {
        cursorPosRef.current = [r, c];
        setHoverPos([r, c]);
    };
    const handleLeaveBoard = () => setHoverPos(null);
    
    const [stagedMove, setStagedMoveState] = useState({ type: 'none', path: [] });
    const [jumpPreview, setJumpPreviewState] = useState(emptyJumpPreview);
    const [invalidPlacement, setInvalidPlacement] = useState(null);
    const [isStackedLayout, setIsStackedLayout] = useState(() => shouldUseStackedLayout());
    const [activePanelIndex, setActivePanelIndex] = useState(1);
    const activePanelIndexRef = useRef(1);
    const swipeContainerRef = useRef(null);
    const [panelFlash, setPanelFlash] = useState(null);
    const flashPanel = useCallback((idx) => { setPanelFlash(idx); setTimeout(() => setPanelFlash(null), 400); }, []);


    const setStagedMove = useCallback((next) => {
        setStagedMoveState(prev => {
            const value = typeof next === 'function' ? next(prev) : next;
            if (areMovesEqual(prev, value)) {
                return prev;
            }
            return value;
        });
    }, []);

    const setJumpPreview = useCallback((next) => {
        setJumpPreviewState(prev => {
            const value = typeof next === 'function' ? next(prev) : next;
            if (arePreviewsEqual(prev, value)) {
                return prev;
            }
            return value;
        });
    }, []);

    const applyPreviewPayload = useCallback((preview, phutballFromMessage = null) => {
        if (!preview || typeof preview !== 'object') {
            return false;
        }
        if (preview.type === 'jump' && Array.isArray(preview.path) && preview.path.length) {
            const path = preview.path.map(pos => Array.isArray(pos) ? [...pos] : pos);
            const start = Array.isArray(preview.start)
                ? [...preview.start]
                : (Array.isArray(phutballFromMessage) ? [...phutballFromMessage] : null);
            setStagedMove({ type: 'jump', path, startPos: start });
            setJumpPreview(start ? buildPreviewFromJumpPath(start, path) : emptyJumpPreview);
            setInvalidPlacement(null);
            return true;
        }
        if (preview.type === 'placement' && Array.isArray(preview.path) && preview.path[0]) {
            const pos = Array.isArray(preview.path[0]) ? [...preview.path[0]] : preview.path[0];
            setStagedMove({ type: 'placement', path: [pos] });
            setJumpPreview(emptyJumpPreview);
            setInvalidPlacement(null);
            return true;
        }
        return false;
    }, [setInvalidPlacement, setJumpPreview, setStagedMove]);

    const handleHistoryPreviewMessage = useCallback((msg) => {
        const source = msg?.source ?? 'linear';
        const messageIndex = typeof msg?.historyIndex === 'number' ? msg.historyIndex : null;
        const pendingRequest = previewRequestRef.current;
        const pendingSource = pendingRequest?.source ?? null;
        const hoverState = historyHoverPreviewRef.current;
        const hoverSource = hoverState?.source ?? null;
        const hoverIndex = typeof hoverState?.index === 'number' ? hoverState.index : null;
        let matchesRequest = false;
        if (pendingSource === 'linear') {
            matchesRequest = source === 'linear'
                && pendingRequest.historyIndex != null
                && messageIndex != null
                && pendingRequest.historyIndex === messageIndex;
        } else if (pendingSource === 'tree') {
            const nodeId = msg?.nodeId ?? null;
            const parentNodeId = msg?.parentNodeId ?? null;
            matchesRequest = source === 'tree'
                && pendingRequest.nodeId === nodeId
                && pendingRequest.parentNodeId === parentNodeId;
        } else if (pendingSource === 'live-hover') {
            matchesRequest = false;
        } else {
            matchesRequest = false;
        }
        const matchesHoverState = Boolean(
            hoverState?.active
            && hoverSource === 'linear'
            && source === 'linear'
            && hoverIndex != null
            && messageIndex != null
            && hoverIndex === messageIndex
        );
        if (source === 'linear' && !matchesRequest && !matchesHoverState) {
            return;
        }
        const isActive = Boolean(msg?.active && Array.isArray(msg?.board));
        if (isActive) {
            setHistoryPreviewState({
                source,
                board: msg.board,
                phutballPos: Array.isArray(msg.phutballPos) ? [...msg.phutballPos] : null,
                currentPlayer: msg.currentPlayer ?? null,
                historyIndex: typeof msg.historyIndex === 'number' ? msg.historyIndex : null,
                nodeId: msg.nodeId ?? null,
                parentNodeId: msg.parentNodeId ?? null,
            });
            // Don't show cursor when just previewing history - only show it when you can actually interact
            // (i.e., at live position or after branching)
            if (!isReplayingRef.current) {
                applyPreviewPayload(msg.preview, msg.phutballPos);
            }
            setTreePreviewActive(source === 'tree');
            return;
        }
        setHistoryPreviewState(null);
        setStagedMove({ type: 'none', path: [] });
        setJumpPreview(emptyJumpPreview);
        setInvalidPlacement(null);
        setTreePreviewActive(false);
    }, [applyPreviewPayload, setTreePreviewActive]);

    const updatePreviewedTreeEdge = useCallback((nextEdge) => {
        const prevEdge = previewedTreeEdgeRef.current;
        if (prevEdge.parentId === nextEdge.parentId && prevEdge.childId === nextEdge.childId) {
            return;
        }
        previewedTreeEdgeRef.current = nextEdge;
        setPreviewedTreeEdge(nextEdge);
    }, [setPreviewedTreeEdge]);

    const nodesById = useMemo(() => {const map = new Map(); const traverse = (n) => { if (!n) return; map.set(n.id, n); (n.children ?? []).forEach(traverse); }; if (gameState.historyTree?.root) traverse(gameState.historyTree.root); return map; }, [gameState.historyTree]);

    const turnStartIndices = useMemo(() => computeTurnStarts(gameState.history), [gameState.history]);

    const currentNodeId = gameState.historyTree?.currentNodeId ?? null;
    const liveNodeId = gameState.historyTree?.liveNodeId ?? null;
    const liveNode = liveNodeId && nodesById.has(liveNodeId) ? nodesById.get(liveNodeId) : null;
    const livePerspectivePlayer = typeof liveNode?.statePlayer === 'number'
        ? liveNode.statePlayer
        : (typeof liveNode?.player === 'number' ? liveNode.player : null);
    const isAtLiveNode = Boolean(currentNodeId && liveNodeId && currentNodeId === liveNodeId);
    
    const historyViewingActive = !isAtLiveNode && !historyBranchMode;
    const branchViewActive = !isAtLiveNode && historyBranchMode;

    const currentNode = nodesById.get(currentNodeId);
    const renderingLivePreview = historyPreviewState?.source === 'live-hover';
    const hoverPreviewActive = Boolean(
        historyPreviewState
        && (historyPreviewState.source === 'linear' || historyPreviewState.source === 'live-hover')
    );
    const visibleBoard = historyPreviewState?.board ?? gameState.board;
    const visiblePhutballPos = historyPreviewState?.phutballPos ?? gameState.phutballPos;
    const visibleCurrentPlayer = historyPreviewState?.currentPlayer ?? gameState.currentPlayer;

    const nodeToMove =
        currentNode?.toMove ??
        currentNode?.playerToMove ??
        visibleCurrentPlayer;
    
    const nodeLastMover = (() => {
        if (typeof currentNode?.lastMover === 'number') {
            return currentNode.lastMover;
        }
        if (nodeToMove === 1) return 2;
        if (nodeToMove === 2) return 1;
        return null;
    })();
    
    const currentHistoryIndex = typeof gameState.historyIndex === 'number' ? gameState.historyIndex : null;
    const currentEntry =
        Array.isArray(gameState.history) && currentHistoryIndex != null
            ? gameState.history[currentHistoryIndex]
            : null;
        
    const entryIsJumpStep = typeof currentEntry === 'string' && currentEntry.includes('Jump');
    const entryPlayer = extractEntryPlayer(currentEntry);

    const stagedMirrorsCurrent = (() => {
        if (!stagedMove || stagedMove.type === 'none') return false;
        const parsed = parseHistoryEntryForPreview(currentEntry, {
            history: gameState.history,
            index: currentHistoryIndex,
        });
        if (!parsed) return false;
        if (parsed.type === 'placement' && stagedMove.type === 'placement') {
            const pos = Array.isArray(stagedMove.path?.[0]) ? stagedMove.path[0] : null;
            return pos && Array.isArray(parsed.position)
                && pos[0] === parsed.position[0] && pos[1] === parsed.position[1];
        }
        if (parsed.type === 'jump' && stagedMove.type === 'jump') {
            const sameLen = Array.isArray(parsed.path) && Array.isArray(stagedMove.path)
                && parsed.path.length === stagedMove.path.length;
            if (!sameLen) return false;
            for (let i = 0; i < parsed.path.length; i += 1) {
                const a = parsed.path[i], b = stagedMove.path[i];
                if (!Array.isArray(a) || !Array.isArray(b) || a[0] !== b[0] || a[1] !== b[1]) return false;
            }
            // Optional: if you persist startPos on stagedMove for jumps, also check it:
            if (Array.isArray(parsed.start) && Array.isArray(stagedMove.startPos)) {
                return parsed.start[0] === stagedMove.startPos[0] && parsed.start[1] === stagedMove.startPos[1];
            }
            return true;
        }
        return false;
    })();

    const hasActivePreview = stagedMove?.type && stagedMove.type !== 'none' && !stagedMirrorsCurrent;

    const shouldHighlightHistoryEntry = Boolean(
        entryPlayer != null &&
        (isReplayingHistory || (historyViewingActive && stagedMirrorsCurrent))
    );

    useEffect(() => {
        if (stagedMove.type !== 'none') {
            return;
        }
        updatePreviewedTreeEdge({ parentId: null, childId: null });
    }, [stagedMove.type, updatePreviewedTreeEdge]);

    useEffect(() => {
        const index = typeof gameState.historyIndex === 'number' ? gameState.historyIndex : null;
        const prev = historyHighlightRef.current;
        if (shouldHighlightHistoryEntry && index != null) {
            if (!prev.should || prev.index !== index) {
                setHistoryHighlightToken((token) => token + 1);
            }
            historyHighlightRef.current = { should: true, index };
        } else {
            historyHighlightRef.current = { should: false, index };
        }
    }, [shouldHighlightHistoryEntry, gameState.historyIndex]);

    const getNodePlayer = useCallback((node) => {
        if (!node) {
            return null;
        }
        const descriptor = typeof node.moveDesc === 'string'
            ? node.moveDesc
            : (typeof node.moveSummary === 'string' ? node.moveSummary : null);
        if (descriptor) {
            const extracted = extractEntryPlayer(descriptor);
            if (extracted != null) {
                return extracted;
            }
        }
        if (typeof node.player === 'number') {
            return node.player;
        }
        if (typeof node.statePlayer === 'number') {
            return node.statePlayer;
        }
        return null;
    }, []);

    const currentNodeMover = typeof currentNode?.player === 'number' ? currentNode.player : null;
    const stagedTreePlayer = useMemo(() => {
        if (!stagedMove || stagedMove.type === 'none') {
            return null;
        }

        const resolvePlayerFromNode = (node) => {
            const descriptor = typeof node?.moveDesc === 'string'
                ? node.moveDesc
                : (typeof node?.moveSummary === 'string' ? node.moveSummary : null);
            if (!descriptor) {
                return null;
            }
            const parsed = parseHistoryEntryForPreview(descriptor);
            if (!parsed) {
                return null;
            }

            if (parsed.type === 'placement' && stagedMove.type === 'placement') {
                const stagedPos = Array.isArray(stagedMove.path?.[0]) ? stagedMove.path[0] : null;
                if (stagedPos && positionsEqual(parsed.position, stagedPos)) {
                    return typeof node.player === 'number' ? node.player : null;
                }
            }

            if (parsed.type === 'jump' && stagedMove.type === 'jump') {
                const stagedPath = Array.isArray(stagedMove.path) ? stagedMove.path : [];
                const parsedPath = Array.isArray(parsed.path) ? parsed.path : [];
                if (parsedPath.length !== stagedPath.length) {
                    return null;
                }
                for (let i = 0; i < parsedPath.length; i += 1) {
                    if (!positionsEqual(parsedPath[i], stagedPath[i])) {
                        return null;
                    }
                }
                if (Array.isArray(parsed.start) && Array.isArray(stagedMove.startPos) && !positionsEqual(parsed.start, stagedMove.startPos)) {
                    return null;
                }
                return typeof node.player === 'number' ? node.player : null;
            }

            return null;
        };

        const seenIds = new Set();
        const candidates = [];
        const addCandidate = (node) => {
            if (!node || typeof node !== 'object') {
                return;
            }
            const key = node.id ?? node.tree_move_string ?? node.moveDesc ?? null;
            if (key != null) {
                if (seenIds.has(key)) {
                    return;
                }
                seenIds.add(key);
            }
            candidates.push(node);
        };
        const addNodeAndChildren = (node) => {
            if (!node) {
                return;
            }
            addCandidate(node);
            if (Array.isArray(node.children)) {
                node.children.forEach(child => addCandidate(child));
            }
        };

        const previewEdge = previewedTreeEdge;
        if (previewEdge?.childId && nodesById.has(previewEdge.childId)) {
            addNodeAndChildren(nodesById.get(previewEdge.childId));
        }
        if (previewEdge?.parentId && nodesById.has(previewEdge.parentId)) {
            addNodeAndChildren(nodesById.get(previewEdge.parentId));
        }

        addNodeAndChildren(currentNode);

        for (let i = 0; i < candidates.length; i += 1) {
            const player = resolvePlayerFromNode(candidates[i]);
            if (player != null) {
                return player;
            }
        }

        return null;
    }, [stagedMove, currentNode, nodesById, previewedTreeEdge]);
    const stagedPlayer = (() => {
        if (!stagedMove || stagedMove.type === 'none') {
            return null;
        }
        if (typeof stagedMove.playerOverride === 'number') {
            return stagedMove.playerOverride;
        }
        if (!isAtLiveNode) {
            return stagedTreePlayer
                ?? entryPlayer
                ?? currentNodeMover
                ?? nodeLastMover
                ?? nodeToMove;
        }
        return null;
    })();

    const previewedTreePlayer = useMemo(() => {
        const childId = previewedTreeEdge?.childId ?? null;
        if (!childId || !nodesById.has(childId)) {
            return null;
        }
        const node = nodesById.get(childId);
        return getNodePlayer(node);
    }, [nodesById, previewedTreeEdge, getNodePlayer]);

    const viewingTreePlayer = useMemo(() => {
        if (!currentNodeId || !nodesById.has(currentNodeId)) {
            return null;
        }
        const node = nodesById.get(currentNodeId);
        return getNodePlayer(node);
    }, [currentNodeId, nodesById, getNodePlayer]);

    const displayPlayer = (() => {
        if (previewPerspectivePlayer != null) {
            return previewPerspectivePlayer;
        }
        if (isReplayingHistory) {
            return entryPlayer ?? currentNodeMover ?? nodeLastMover ?? nodeToMove;
        }

        if (previewedTreePlayer != null) {
            return previewedTreePlayer;
        }

        if (stagedPlayer != null) {
            return stagedPlayer;
        }

        if (viewingTreePlayer != null && !isAtLiveNode) {
            return viewingTreePlayer;
        }

        if (isAtLiveNode) {
            // At live node, perspective depends on turn mode:
            // - In turn mode: next player's perspective (ready to play)
            // - Not in turn mode: mover's perspective (viewing the HALT move)
            if (isInTurnMode) {
                return gameState.currentPlayer;
            } else {
                return viewingTreePlayer ?? gameState.currentPlayer;
            }
        }

        if (historyBranchMode || historyViewingActive) {
            return currentNodeMover ?? entryPlayer ?? nodeLastMover ?? nodeToMove;
        }

        // Fallback for other cases - use who made the last move
        return currentNodeMover ?? entryPlayer ?? nodeLastMover ?? nodeToMove;
    })();

    // Puzzle mode locks the perspective to the player who's actually making
    // the move (the responder, in threat puzzles; the same as
    // puzzleMetadata.player for everything else). The user always views the
    // board with their own attacking direction toward the top and their
    // defensive endzone at the bottom, so a threat correctly reads as an
    // attack arriving at the bottom of the screen (their endzone is what's
    // being attacked).
    const puzzlePerspectivePlayer =
        config?.gameMode === 'puzzle'
            ? (puzzleMetadata?.responder ?? puzzleMetadata?.player ?? null)
            : null;
    const effectivePerspective = puzzlePerspectivePlayer
        ?? (displayPlayer ?? gameState.currentPlayer ?? nodeToMove);

    const boardRowCount = Array.isArray(visibleBoard) ? visibleBoard.length : 0;
    const boardColCount = boardRowCount > 0 && Array.isArray(visibleBoard[0]) ? visibleBoard[0].length : 0;

    const makeIndicator = (position) => formatBoardCoordinate({
        position,
        numRows: boardRowCount,
        numCols: boardColCount,
        coordSystem,
        viewPerspective: effectivePerspective,
    });

    const cursorIndicator = makeIndicator(cursorPos);
    const hoverIndicator = makeIndicator(hoverPos);

    useEffect(() => {
        if (!isReplayingHistory) return;
        if (stagedMove.type !== 'jump') return;
        if (entryIsJumpStep) return;
        setStagedMove({ type: 'none', path: [] });
        setJumpPreview(emptyJumpPreview);
        setInvalidPlacement(null);
    }, [isReplayingHistory, stagedMove.type, entryIsJumpStep, setStagedMove, setJumpPreview, setInvalidPlacement]);

    useEffect(() => { isReplayingRef.current = isReplayingHistory; }, [isReplayingHistory]);

    useEffect(() => {
        if (isAtLiveNode || isReplayingHistory) {
            setHistoryBranchMode(false);
        }
    }, [isAtLiveNode, isReplayingHistory]);

    useEffect(() => {
        latestStateRef.current = gameState;
    }, [gameState]);

    // Clear any staged move the instant replay begins
    useEffect(() => {
    if (isReplayingHistory) {
        selectedHistoryIndexRef.current = null;
        setStagedMove({ type: 'none', path: [] });
        setJumpPreview(emptyJumpPreview);
        setInvalidPlacement(null);
    }
    }, [isReplayingHistory]);

    const initialFitDoneRef = useRef(false);
    

    useEffect(() => {
        if (!initialFitDoneRef.current && controlsReady && controlsRef.current) {
            controlsRef.current.fitAndCenter();
            initialFitDoneRef.current = true;
        }
    }, [controlsReady, gameState.history?.length]);

    useEffect(() => {
        if (Array.isArray(gameState.history) && gameState.history.length === 0) {
            initialFitDoneRef.current = false;
        }
    }, [gameState.history]);

    useEffect(() => {
        if (!isStackedLayout) {
            settlePagerOffset();
        }
    }, [isStackedLayout, settlePagerOffset]);

    useEffect(() => {
        if (!isStackedLayout) {
            return;
        }
        setSwipeOffsetPct(0);
        setIsPagerDragging(false);
    }, [activePanelIndex, isStackedLayout]);

    useEffect(() => {
        activePanelIndexRef.current = activePanelIndex;
    }, [activePanelIndex]);

    useEffect(() => {
        cursorPosRef.current = cursorPos;
    }, [cursorPos]);

    useEffect(() => {
        if (cursorPos == null && Array.isArray(gameState.phutballPos)) {
            setCursorPos(gameState.phutballPos);
        }
    }, [cursorPos, gameState.phutballPos]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return () => {};
        }
        const handleResize = () => {
            setIsStackedLayout(shouldUseStackedLayout());
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        if (!isStackedLayout) {
            return () => {};
        }
        const container = swipeContainerRef.current;
        if (!container) {
            return () => {};
        }
        let pointerId = null;
        let startX = 0;
        let startY = 0;
        let tracking = false;
        let requiredDistance = 0;

        const resetTracking = () => {
            pointerId = null;
            tracking = false;
            requiredDistance = 0;
        };

        const handlePointerDown = (event) => {
            logSwipeDebug('pointerdown', {
                stacked: isStackedLayout,
                tracking,
                target: event.target?.className ?? 'unknown',
            });
            if (!isStackedLayout || tracking) {
                logSwipeDebug('skip pointerdown', { reason: 'not-stacked-or-tracking' });
                return;
            }
            if (event.pointerType === 'mouse' && event.buttons !== 1) {
                logSwipeDebug('skip pointerdown', { reason: 'mouse-without-primary' });
                return;
            }
            const boardLayout = event.target?.closest('.board-layout-container');
            if (boardLayout) {
                logSwipeDebug('skip pointerdown', { reason: 'board-layout-area' });
                return;
            }
            pointerId = event.pointerId;
            startX = event.clientX;
            startY = event.clientY;
            requiredDistance = computeSwipeThreshold(container, POINTER_SWIPE_FRACTION);
            tracking = true;
            setIsPagerDragging(true);
            setSwipeOffsetPct(0);
            logSwipeDebug('begin tracking', {
                startX,
                startY,
                target: event.target?.className ?? 'unknown',
            });
        };

        const handlePointerMove = (event) => {
            if (!tracking || (pointerId != null && event.pointerId !== pointerId)) {
                return;
            }
            const dx = event.clientX - startX;
            const width = container.clientWidth || window.innerWidth || 1;
            let offsetPct = Math.max(-100, Math.min(100, (dx / width) * 100));
            // Constrain swipe at boundaries: no swiping past edges
            const panelIdx = activePanelIndexRef.current;
            if (panelIdx === 0 && offsetPct > 0) {
                offsetPct = 0; // Can't swipe right on leftmost panel
            } else if (panelIdx === PANEL_CONFIG.length - 1 && offsetPct < 0) {
                offsetPct = 0; // Can't swipe left on rightmost panel
            }
            setSwipeOffsetPct(offsetPct);
        };

        const handlePointerUp = (event) => {
            if (!tracking || (pointerId != null && event.pointerId !== pointerId)) {
                resetTracking();
                settlePagerOffset();
                return;
            }
            const dx = event.clientX - startX;
            const dy = event.clientY - startY;
            const threshold = requiredDistance > 0
                ? requiredDistance
                : computeSwipeThreshold(container, POINTER_SWIPE_FRACTION);
            logSwipeDebug('pointerup', { dx, dy, threshold });
            if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) >= threshold) {
                const direction = dx < 0 ? 1 : -1;
                resetTracking();
                setActivePanelIndex(prev => clampPanelIndex(prev + direction));
                settlePagerOffset();
                return;
            }
            logSwipeDebug('swipe ignored', {
                reason: 'insufficient-distance-or-vertical',
                absDx: Math.abs(dx),
                absDy: Math.abs(dy),
            });
            resetTracking();
            settlePagerOffset();
        };

        const handlePointerCancel = () => {
            if (!tracking) {
                return;
            }
            resetTracking();
            settlePagerOffset();
        };

        container.addEventListener('pointerdown', handlePointerDown, { passive: true });
        container.addEventListener('pointermove', handlePointerMove, { passive: true });
        container.addEventListener('pointerup', handlePointerUp);
        container.addEventListener('pointercancel', handlePointerCancel);
        container.addEventListener('pointerleave', handlePointerCancel);

        return () => {
            container.removeEventListener('pointerdown', handlePointerDown);
            container.removeEventListener('pointermove', handlePointerMove);
            container.removeEventListener('pointerup', handlePointerUp);
            container.removeEventListener('pointercancel', handlePointerCancel);
            container.removeEventListener('pointerleave', handlePointerCancel);
        };
    }, [isStackedLayout, settlePagerOffset]);

    useEffect(() => {
        if (!isStackedLayout) {
            return () => {};
        }
        const container = swipeContainerRef.current;
        if (!container) {
            return () => {};
        }
        const handleWheel = (event) => {
            const indicatorTarget = event.target?.closest?.('.panel-indicator-bar');
            const isShift = event.shiftKey;
            logSwipeDebug('wheel gesture', {
                indicator: Boolean(indicatorTarget),
                shift: isShift,
                deltaX: event.deltaX,
                deltaY: event.deltaY,
            });
            const dominantHorizontal = Math.abs(event.deltaX) >= Math.abs(event.deltaY);
            if (!indicatorTarget && !isShift && !dominantHorizontal) {
                return;
            }
            const gestureState = wheelGestureStateRef.current;
            const now = Date.now();
            if (gestureState.lockedUntil && now < gestureState.lockedUntil) {
                event.preventDefault();
                return;
            }
            const width = container.clientWidth || window.innerWidth || 1;
            const thresholdPx = computeSwipeThreshold(container, WHEEL_SWIPE_FRACTION);
            const scrollDelta = -event.deltaX;
            gestureState.accumX += scrollDelta;
            // Constrain at boundaries
            const panelIdx = activePanelIndexRef.current;
            if (panelIdx === 0 && gestureState.accumX > 0) {
                gestureState.accumX = 0; // Can't swipe right on leftmost panel
            } else if (panelIdx === PANEL_CONFIG.length - 1 && gestureState.accumX < 0) {
                gestureState.accumX = 0; // Can't swipe left on rightmost panel
            }
            logSwipeDebug('wheel accum', {
                accumX: gestureState.accumX,
                threshold: thresholdPx,
            });
            let offsetPct = Math.max(-100, Math.min(100, (gestureState.accumX / width) * 100));
            // Also constrain the visual offset at boundaries
            if (panelIdx === 0 && offsetPct > 0) {
                offsetPct = 0;
            } else if (panelIdx === PANEL_CONFIG.length - 1 && offsetPct < 0) {
                offsetPct = 0;
            }
            setIsPagerDragging(true);
            setSwipeOffsetPct(offsetPct);
            if (Math.abs(gestureState.accumX) >= thresholdPx) {
                const direction = gestureState.accumX < 0 ? 1 : -1;
                gestureState.accumX = 0;
                gestureState.lockedUntil = now + WHEEL_COOLDOWN_MS;
                setActivePanelIndex(prev => clampPanelIndex(prev + direction));
                settlePagerOffset();
                event.preventDefault();
                return;
            }
            gestureState.lockedUntil = 0;
            event.preventDefault();
        };
        container.addEventListener('wheel', handleWheel, { passive: false });
        return () => {
            container.removeEventListener('wheel', handleWheel);
            const cleanupGestureState = wheelGestureStateRef.current;
            cleanupGestureState.lockedUntil = 0;
            cleanupGestureState.accumX = 0;
            settlePagerOffset();
        };
    }, [isStackedLayout, settlePagerOffset]);

    useEffect(() => {
    const onKey = (e) => {
        const k = e.key?.toLowerCase?.();

        // Handle Z toggle even if controls aren't ready yet
        if (k === 'z') {
        e.preventDefault();
        setLockCameraDuringReplay((prev) => !prev);
        return;
        }

        const c = controlsRef.current;
        if (!c) return;

        if (k === 'f') {
        e.preventDefault();
        c.fitAndCenter();
        } else if (k === 'c') {
        e.preventDefault();
        c.centerAtCurrentScale();
        }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    }, []);


    const toggleCoordSystem = useCallback((event) => {
        setCoordSystem(prevSystem => (prevSystem === 'endzone' ? 'matrix' : 'endzone'));
        if (event && typeof event.currentTarget?.blur === 'function') {
            event.currentTarget.blur();
        }
    }, []);

    useEffect(() => {
        if (skipFirstEffectRef.current) {
            skipFirstEffectRef.current = false;
            return;
        }

        // Skip WebSocket connection for local games (engine handles state)
        if (isLocalGame) return;

        const url =
            process.env.REACT_APP_WS_URL ||
            `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.hostname}:8000/ws/1`;

        const ws = new WebSocket(url);
        socketRef.current = ws;

        ws.onopen = () => {
            console.debug('[ws] open');
            setStatus('Connected');

            // If we have session data to restore, send that instead of new_game
            if (restoreSessionData && !sessionRestoreSent.current) {
                sessionRestoreSent.current = true;
                console.debug('[ws] restoring session...');
                ws.send(JSON.stringify({
                    action: 'restore_session',
                    sessionData: restoreSessionData,
                }));
            } else {
                // Send game configuration to server
                const initMessage = {
                    action: 'new_game',
                    config: {
                        rows: configRows,
                        cols: configCols,
                        p1: config?.p1 ?? 'Human',
                        p2: config?.p2 ?? 'Human',
                        modelName: config?.modelName ?? null,
                        gameMode: config?.gameMode ?? 'local_multiplayer',
                        apiKey: config?.apiKey ?? null,
                        llmPlanMode: config?.llmPlanMode ?? 'single',
                        nnVisualization: config?.nnVisualization ?? false,
                        puzzle: config?.puzzle ?? null,
                    }
                };
                console.debug('[ws] sending init config', initMessage);
                ws.send(JSON.stringify(initMessage));
            }
        };
        ws.onclose = () => {
            console.debug('[ws] closed');
            setStatus('Disconnected');
            pendingMoveRef.current = null;
            setAwaitingAck(false);
        };
        ws.onerror = (err) => {
            console.error('[ws] error', err);
            setStatus('Connection error');
            setAwaitingAck(false);
        };
        ws.onmessage = (evt) => {
            try {
                const msg = JSON.parse(evt.data);
                console.debug('[ws] received', msg);
                if (msg.type === 'history_preview') {
                    handleHistoryPreviewMessage(msg);
                    return;
                }
                if (msg.type === 'game_state' && Array.isArray(msg.board)) {
                    const historyEntries = Array.isArray(msg.history) ? msg.history : [];
                    const entryCount = historyEntries.length;
                    if (!historyHoverPreviewRef.current.active) {
                        setHistoryPreviewState(null);
                    }
                    setGameState({
                        board: msg.board,
                        currentPlayer: msg.currentPlayer,
                        winner: msg.winner ?? 0,
                        history: historyEntries,
                        historyIndex: msg.historyIndex ?? 0,
                        phutballPos: msg.phutballPos ?? initialPhutballPos,
                        historyTree: msg.historyTree ?? null,
                        pieceTilt: msg.pieceTilt ?? null,
                        statsHistory: msg.statsHistory ?? [],
                        logHistory: msg.logHistory ?? [],
                        initialBoardText: msg.initialBoardText ?? null,
                    });

                    // Save session to localStorage for persistence across refreshes
                    if (msg.sessionData) {
                        saveSession(msg.sessionData, config);
                    }

                    if (typeof msg.historyIndex === 'number') {
                        const clampIndex = (value) => {
                            if (!Number.isFinite(value)) return 0;
                            if (value < 0) return 0;
                            if (value > entryCount) return entryCount;
                            return value;
                        };
                        const preferredIndex = typeof selectedHistoryIndexRef.current === 'number'
                            ? selectedHistoryIndexRef.current
                            : msg.historyIndex;
                        setHistoryFocusIndex(prev => {
                            if (pendingLiveFocusRef.current) {
                                return clampIndex(entryCount);
                            }
                            return clampIndex(preferredIndex);
                        });
                    }
                    if (Array.isArray(msg.phutballPos) && !positionsEqual(cursorPosRef.current, msg.phutballPos)) {
                        setCursorPos(msg.phutballPos);
                    }

                    let skipPreviewThisMessage = false;

                    if (clearStageOnNextStateRef.current) {
                        clearStageOnNextStateRef.current = false;
                        setStagedMove({ type: 'none', path: [] });
                        setJumpPreview(emptyJumpPreview);
                        setInvalidPlacement(null);
                        skipPreviewThisMessage = true;
                    }

                    const selectedIndex = selectedHistoryIndexRef.current;
                    const indexMatches = typeof selectedIndex === 'number'
                        && typeof msg.historyIndex === 'number'
                        && selectedIndex === msg.historyIndex;

                    const isReplaying = isReplayingRef.current;
                    const preview = msg.preview;
                    let previewHandled = false;

                    if (!skipPreviewThisMessage && !isReplaying && preview && typeof preview === 'object') {
                        previewHandled = applyPreviewPayload(preview, msg.phutballPos);
                    }

                    const currentTreeNodeId = msg.historyTree?.currentNodeId ?? null;
                    const pendingSelection = pendingTreeSelectionRef.current;
                    const pendingTreeNodeId = pendingSelection?.nodeId ?? null;
                    const highlightPendingSelection = pendingSelection?.highlight !== false;
                    const liveNodeId = msg.historyTree?.liveNodeId ?? null;
                    const isAtLiveNode = currentTreeNodeId != null && liveNodeId != null && currentTreeNodeId === liveNodeId;

                    if (isAtLiveNode && pendingLiveFocusRef.current) {
                        pendingLiveFocusRef.current = false;
                        // Don't clear perspective when entering turn mode - we want to keep
                        // the next player's perspective that was set in handleGoLive
                        if (!enteringTurnModeRef.current) {
                            setPreviewPerspectivePlayer(null);
                        }
                        enteringTurnModeRef.current = false;
                    }

                    if (!highlightPendingSelection && pendingTreeNodeId && currentTreeNodeId !== pendingTreeNodeId) {
                        setStagedMove({ type: 'none', path: [] });
                        setJumpPreview(emptyJumpPreview);
                        setInvalidPlacement(null);
                    }

                    if (!previewHandled && pendingTreeNodeId && currentTreeNodeId === pendingTreeNodeId) {
                        const historyIdx = msg.historyIndex;
                        const historyEntry = Array.isArray(msg.history) && typeof historyIdx === 'number'
                            ? msg.history[historyIdx]
                            : undefined;
                        const entryPlayerOverride = extractEntryPlayer(historyEntry);
                        const parsed = parseHistoryEntryForPreview(historyEntry, {
                            history: msg.history,
                            index: historyIdx,
                        });
                        if (highlightPendingSelection) {
                            if (parsed?.type === 'jump' && parsed.path?.length && parsed.start) {
                                setStagedMove({ type: 'jump', path: parsed.path, startPos: parsed.start, playerOverride: entryPlayerOverride ?? undefined });
                                setJumpPreview(buildPreviewFromJumpPath(parsed.start, parsed.path));
                                setInvalidPlacement(null);
                                previewHandled = true;
                            } else if (parsed?.type === 'placement' && Array.isArray(parsed.position)) {
                                setStagedMove({ type: 'placement', path: [parsed.position], playerOverride: entryPlayerOverride ?? undefined });
                                setJumpPreview(emptyJumpPreview);
                                setInvalidPlacement(null);
                                previewHandled = true;
                            }
                        } else {
                            setStagedMove({ type: 'none', path: [] });
                            setJumpPreview(emptyJumpPreview);
                            setInvalidPlacement(null);
                            previewHandled = true;
                        }
                        pendingTreeSelectionRef.current = null;
                    } else if (pendingTreeNodeId && currentTreeNodeId === pendingTreeNodeId) {
                        pendingTreeSelectionRef.current = null;
                    }

                    if (!previewHandled && indexMatches && Array.isArray(msg.history) && msg.history[selectedIndex]) {
                        if (isReplaying) {
                            // Don't mirror the current entry as a staged preview during replay (prevents flicker)
                            setStagedMove({ type: 'none', path: [] });
                            setJumpPreview(emptyJumpPreview);
                        } else {
                            const entryPlayerOverride = extractEntryPlayer(msg.history[selectedIndex]);
                            const entry = parseHistoryEntryForPreview(msg.history[selectedIndex], {
                                history: msg.history,
                                index: selectedIndex,
                            });
                            if (entry?.type === 'jump' && entry.path?.length) {
                            setStagedMove({ type: 'jump', path: entry.path, startPos: entry.start, playerOverride: entryPlayerOverride ?? undefined });
                            setJumpPreview(buildPreviewFromJumpPath(entry.start, entry.path));
                            setInvalidPlacement(null);
                            } else if (entry?.type === 'placement' && Array.isArray(entry.position)) {
                            setStagedMove({ type: 'placement', path: [entry.position], playerOverride: entryPlayerOverride ?? undefined });
                            setJumpPreview(emptyJumpPreview);
                            setInvalidPlacement(null);
                            } else {
                            setStagedMove({ type: 'none', path: [] });
                            setJumpPreview(emptyJumpPreview);
                            }
                        }
                        previewHandled = true;
                    }

                    if (!previewHandled && isAtLiveNode && !historyHoverPreviewRef.current.active) {
                        selectedHistoryIndexRef.current = null;
                        setStagedMove({ type: 'none', path: [] });
                        setJumpPreview(emptyJumpPreview);
                        setInvalidPlacement(null);
                        previewHandled = true;
                    }

                    if (!previewHandled && selectedHistoryIndexRef.current == null && pendingTreeSelectionRef.current == null) {
                        if (!historyHoverPreviewRef.current.active) {
                            setStagedMove({ type: 'none', path: [] });
                            setJumpPreview(emptyJumpPreview);
                            setInvalidPlacement(null);
                        }
                    }
                } else if (msg.type === 'puzzle_metadata') {
                    // Server seeded a curriculum puzzle. Stash the canonical
                    // solution for the human-watcher LogPanel banner; this
                    // stays out of the LLM prompt path entirely.
                    setPuzzleMetadata({
                        num_jumps: msg.num_jumps,
                        player: msg.player,
                        seed: msg.seed,
                        canonical_landings: msg.canonical_landings || [],
                    });
                } else if (msg.type === 'move_result') {
                    setAwaitingAck(false);
                    const { success, moveType } = msg;
                    if (success) {
                        selectedHistoryIndexRef.current = null;
                        pendingMoveRef.current = null;
                        setStagedMove({ type: 'none', path: [] });
                        setJumpPreview(emptyJumpPreview);
                        setInvalidPlacement(null);
                        // Clear any preview perspective so we show current player's view
                        setPreviewPerspectivePlayer(null);
                    } else if (pendingMoveRef.current) {
                        console.warn('Move rejected by server', msg);
                        const restore = JSON.parse(JSON.stringify(pendingMoveRef.current));
                        pendingMoveRef.current = restore;
                        setStagedMove(restore);
                        if (restore.type === 'jump') {
                            const latest = latestStateRef.current;
                            setJumpPreview(evaluateJumpSequence(latest.board, latest.phutballPos, restore.path));
                            setInvalidPlacement(null);
                        } else {
                            setJumpPreview(emptyJumpPreview);
                            if (restore.path && restore.path[0]) {
                                setInvalidPlacement(restore.path[0]);
                            }
                        }
                    }
                } else if (msg.type === 'nn_data') {
                    const { nodeId, status, data, error } = msg;

                    // Update NN data status
                    setNnDataStatus(prev => {
                        const next = new Map(prev);
                        next.set(nodeId, status);
                        return next;
                    });

                    // If data is complete, store it
                    if (status === 'Complete' && data) {
                        setNnData(data);
                    } else if (status === 'Failed') {
                        console.error(`NN data fetch failed for node ${nodeId}:`, error);
                    }
                } else if (msg.type === 'llm_status' || msg.type === 'ai_status') {
                    console.debug('[ws] AI status:', msg.status, msg.provider, msg.model);
                    if (msg.status === 'thinking') {
                        setLlmThinking(true);
                        setLlmError(null);
                        llmRequestPending.current = true;
                    }
                } else if (msg.type === 'llm_move_result' || msg.type === 'ai_move_result') {
                    console.debug('[ws] AI move result:', msg);
                    llmRequestPending.current = false;
                    setLlmThinking(false);

                    if (!msg.success) {
                        console.error('[ws] AI move failed:', msg.error);
                        setLlmError(msg.error || 'AI failed to generate a valid move');
                    } else {
                        setLlmError(null);
                        console.debug('[ws] AI reasoning:', msg.reasoning);
                    }
                }
            } catch (e) {
                console.error('Bad message from server:', e);
            }
        };

        return () => {
            try { ws.close(); } catch {}
        };
        }, [applyPreviewPayload, handleHistoryPreviewMessage]);

    const sendAction = useCallback((action) => {
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
            console.debug('[ws] sending', action);
            socketRef.current.send(JSON.stringify(action));
            return true;
        }
        console.warn('[ws] send skipped; socket not ready', action);
        return false;
    }, []);

    // ── Local Game Engine (for local multiplayer without server) ──────

    // Compute field position and piece tilt from engine board state.
    // Returns { fieldPosition: number, pieceTilt: number } where values
    // range from -1 (P1 advantage) to 1 (P2 advantage).
    const computeLocalStats = useCallback((engine) => {
        const { board, phutballPos, rows, cols } = engine;
        const ballRow = phutballPos[0];
        const midRow = Math.floor(rows / 2);

        // Field position: how far the ball is from center toward each endzone
        // P1's endzone is row 0, P2's endzone is rows-1
        // Normalize to -1..1: negative = ball toward P2's endzone (P1 advantage)
        const fieldPosition = midRow > 0 ? (ballRow - midRow) / midRow : 0;

        // Piece tilt: ratio of stones above vs below the ball
        let stonesAbove = 0; // rows < ballRow (toward P1 endzone)
        let stonesBelow = 0; // rows > ballRow (toward P2 endzone)
        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                if (board[r][c] === 1) { // PHUTBALL_MAN
                    if (r < ballRow) stonesAbove++;
                    else if (r > ballRow) stonesBelow++;
                }
            }
        }
        const totalStones = stonesAbove + stonesBelow;
        // Normalize: positive = more stones below ball (P2 side), negative = more above (P1 side)
        const pieceTilt = totalStones > 0 ? (stonesBelow - stonesAbove) / totalStones : 0;

        return { fieldPosition, pieceTilt, value: 0 };
    }, []);

    const buildGameStateFromEngine = useCallback((engine, history, logHistory, initialBoardText) => {
        const treeRoot = localTreeRootRef.current;
        const treeCurrent = localTreeCurrentRef.current;
        const statsHistory = localStatsHistoryRef.current;

        // Compute current piece tilt from board state as { p1: %, p2: % }
        let pieceTilt = null;
        const { board, phutballPos, rows, cols } = engine;
        const ballRow = phutballPos[0];
        let stonesAbove = 0;
        let stonesBelow = 0;
        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                if (board[r][c] === 1) {
                    if (r < ballRow) stonesAbove++;
                    else if (r > ballRow) stonesBelow++;
                }
            }
        }
        const totalStones = stonesAbove + stonesBelow;
        if (totalStones > 0) {
            pieceTilt = {
                p1: Math.round(100 * stonesAbove / totalStones),
                p2: Math.round(100 * stonesBelow / totalStones),
            };
        }

        return {
            board: engine.board.map(row => [...row]),
            currentPlayer: engine.currentPlayer,
            winner: engine.winner,
            history: [...history],
            historyIndex: history.length,
            phutballPos: [...engine.phutballPos],
            historyTree: treeRoot ? {
                root: treeRoot,
                currentNodeId: treeCurrent?.id ?? treeRoot.id,
                liveNodeId: treeCurrent?.id ?? treeRoot.id,
            } : { currentNodeId: 'live', liveNodeId: 'live', nodes: [] },
            pieceTilt,
            statsHistory: [...statsHistory],
            logHistory: [...logHistory],
            initialBoardText: initialBoardText ?? null,
        };
    }, []);

    const processLocalMove = useCallback((movePayload, reasoning = null, llmStats = null) => {
        const engine = engineRef.current;
        if (!engine) return false;

        const playerBefore = engine.currentPlayer;
        const phutballPosBefore = [...engine.phutballPos];
        const isHalt = movePayload.type === 'jump' && movePayload.path.length === 0;
        const snapshots = localNodeSnapshotsRef.current;
        // Snapshot the engine state before any mutation so the visual
        // animation can replay the jump hop-by-hop from the pre-move board.
        const preSnapshot = engine.getSnapshot();

        // Apply moves step-by-step (not atomically) to capture per-node snapshots
        let parentNode = localTreeCurrentRef.current;
        const logHistory = localLogHistoryRef.current;
        const turnIndex = logHistory.length + 1;

        if (isHalt) {
            const result = engine.applyHalt();
            if (!result.success) { console.warn('[local] Halt rejected:', result.reason); return false; }
        } else if (movePayload.type === 'placement') {
            const result = engine.applyPlacement(movePayload.path[0]);
            if (!result.success) { console.warn('[local] Placement rejected:', result.reason); return false; }

            const [r, c] = movePayload.path[0];
            const nodeDesc = `P${playerBefore}: Place at (${r}, ${c})`;
            const uid = ++localTreeUidRef.current;
            const node = createTreeNode(uid, nodeDesc, playerBefore, engine.currentPlayer, parentNode.id, false, false, turnIndex, 0);
            parentNode.children.push(node);
            snapshots.set(node.id, engine.getSnapshot());
            localTreeCurrentRef.current = node;
        } else if (movePayload.type === 'jump' && movePayload.path.length > 0) {
            // Apply individual hops, saving snapshot per node
            let prev = phutballPosBefore;
            for (let i = 0; i < movePayload.path.length; i++) {
                const target = movePayload.path[i];
                const result = engine.applyJump(target);
                if (!result.success) { console.warn(`[local] Jump hop ${i + 1} rejected:`, result.reason); return false; }

                const hopDesc = `P${playerBefore}: Jump (${prev[0]}, ${prev[1]}) -> (${target[0]}, ${target[1]})`;
                const uid = ++localTreeUidRef.current;
                const node = createTreeNode(uid, hopDesc, playerBefore, playerBefore, parentNode.id, true, false, turnIndex, 0);
                parentNode.children.push(node);
                snapshots.set(node.id, engine.getSnapshot());
                parentNode = node;
                prev = target;

                // If jump caused a win, skip halt
                if (result.terminated) {
                    localTreeCurrentRef.current = node;
                    break;
                }
            }

            // Halt (unless game already won during jump)
            if (engine.winner === 0) {
                const haltResult = engine.applyHalt();
                if (!haltResult.success) { console.warn('[local] Halt rejected:', haltResult.reason); return false; }

                const haltDesc = `P${playerBefore}: Halt jump sequence`;
                const haltUid = ++localTreeUidRef.current;
                const haltNode = createTreeNode(haltUid, haltDesc, playerBefore, engine.currentPlayer, parentNode.id, false, true, turnIndex, 0);
                parentNode.children.push(haltNode);
                snapshots.set(haltNode.id, engine.getSnapshot());
                localTreeCurrentRef.current = haltNode;
            }
        }

        // Build linear history entry
        const history = localHistoryRef.current;
        const moveNum = history.length;
        let desc;
        if (isHalt) {
            desc = `${moveNum}. P${playerBefore}: HALT`;
        } else if (movePayload.type === 'placement') {
            const [r, c] = movePayload.path[0];
            desc = `${moveNum}. P${playerBefore}: Place at (${r}, ${c})`;
        } else if (movePayload.type === 'jump') {
            const allPositions = [phutballPosBefore, ...movePayload.path];
            const pathStr = allPositions.map(([r, c]) => `(${r}, ${c})`).join(' -> ');
            desc = `${moveNum}. P${playerBefore}: Jump ${pathStr}`;
        }
        history.push(desc);

        // Build log entry
        const boardText = engine.textRender(playerBefore);
        const action = { moveDesc: desc, boardText, reasoning, thinkingTime: llmStats?.thinkingTime ?? null, tokenUsage: llmStats?.tokenUsage ?? null };
        logHistory.push({ turnIndex, player: playerBefore, actions: [action], turnReasoning: null });

        // Save snapshot for potential undo
        localSnapshotsRef.current.push(engine.getSnapshot());

        // Append stats history entry for StatsPlot (value filled in asynchronously by the value head)
        {
            const entry = computeLocalStats(engine);
            localStatsHistoryRef.current.push(entry);
            queueValueEval(entry, engine.getSnapshot());
        }

        // Persist to localStorage for reload recovery (skip standalone halts;
        // applyMove includes implicit halt). The reasoning/thinkingTime/tokens
        // are stored alongside the move payload so the LogPanel restores the
        // per-move LLM trace on reload instead of showing blank entries.
        if (!isHalt) {
            localMovesRef.current.push({
                type: movePayload.type,
                path: movePayload.path,
                reasoning,
                thinkingTime: llmStats?.thinkingTime ?? null,
                tokenUsage: llmStats?.tokenUsage ?? null,
            });
            saveLocalGame(localMovesRef.current, config);
        }

        // Update React state. Multi-hop jumps animate hop-by-hop using a
        // temp engine clone replayed from the pre-move snapshot. Placements
        // and standalone halts snap straight to the post-move state.
        const finalGameState = buildGameStateFromEngine(engine, history, logHistory, localInitialBoardTextRef.current);
        const isMultiVisibleJump = movePayload.type === 'jump' && movePayload.path?.length > 0;
        if (isMultiVisibleJump) {
            const animEngine = new PhutballEngine(engine.rows, engine.cols);
            animEngine.loadSnapshot(preSnapshot);
            const frames = [{
                board: animEngine.board.map(r => [...r]),
                phutballPos: [...animEngine.phutballPos],
            }];
            for (const target of movePayload.path) {
                const r = animEngine.applyJump(target);
                frames.push({
                    board: animEngine.board.map(r2 => [...r2]),
                    phutballPos: [...animEngine.phutballPos],
                });
                if (r.terminated) break;
            }
            // First paint shows the pre-move position with the new history pane.
            // currentPlayer stays at the mover so the board does NOT rotate
            // until every hop has been animated — concurrent rotation while
            // the ball was hopping made it look like the ball was floating
            // from screen center to its landing cell.
            // winner is force-zeroed for the animation frames so the
            // GameOverModal (which triggers on winner != 0) doesn't pop up
            // mid-jump and let the user dismiss it while ticks are still
            // pending. The final tick restores the true winner via
            // setGameState(finalGameState).
            setGameState({ ...finalGameState, board: frames[0].board, phutballPos: frames[0].phutballPos, currentPlayer: playerBefore, winner: 0 });
            // Show the planned path. startPos is cleared so jumpStart in
            // GameBoard falls through to phutballPos — the trace then renders
            // as [currentBallPos, ...remainingHops]. Revisited cells stay
            // visible until the final hop that lands on them is consumed.
            // Set unconditionally so AI/programmatic moves get the same
            // telegraphed path as moves the user staged manually.
            setStagedMove({ type: 'jump', path: [...movePayload.path], startPos: null });
            const HOP_DELAY_MS = 350;
            // Capture the generation for this animation. If handleNewGame (or
            // any other reset path) bumps animGenRef while ticks are pending,
            // remaining ticks become no-ops.
            animGenRef.current += 1;
            const animGen = animGenRef.current;
            let idx = 1;
            const tick = () => {
                if (animGenRef.current !== animGen) return; // aborted
                if (idx >= frames.length) {
                    // Apply the real finalGameState now — currentPlayer flips
                    // to the next mover, which kicks off the board rotation.
                    setGameState(finalGameState);
                    setStagedMove({ type: 'none', path: [] });
                    setJumpPreview(emptyJumpPreview);
                    return;
                }
                const frame = frames[idx];
                setGameState(prev => prev ? { ...prev, board: frame.board, phutballPos: frame.phutballPos } : prev);
                // Drop the segment we just travelled from the visible trace
                // and recompute the ghost overlay so consumed stones
                // disappear hop-by-hop instead of all at once at the end.
                setStagedMove(prev => {
                    if (prev?.type !== 'jump') return prev;
                    const remaining = prev.path.slice(1);
                    setJumpPreview(remaining.length > 0
                        ? buildPreviewFromJumpPath(frame.phutballPos, remaining)
                        : emptyJumpPreview);
                    return { ...prev, path: remaining };
                });
                idx += 1;
                setTimeout(tick, HOP_DELAY_MS);
            };
            setTimeout(tick, HOP_DELAY_MS);
        } else {
            setGameState(finalGameState);
            setStagedMove({ type: 'none', path: [] });
            setJumpPreview(emptyJumpPreview);
        }

        // Single-turn puzzle evaluation. Every puzzle ends after one
        // committed move by the responder. We capture the verdict in
        // puzzleEvalResult; the GameOverModal triggers off it for
        // puzzle-mode games even when engine.winner == 0 (e.g. threat
        // puzzles where the response is a placement, not a winning jump).
        if (config?.gameMode === 'puzzle') {
            puzzleAttemptCommittedRef.current = true;
            const meta = puzzleMetadataRef.current;
            const evalAndShow = () => {
                const result = evaluatePuzzleAfterMove(engine, meta, movePayload);
                setPuzzleEvalResult(result);
            };
            if (isMultiVisibleJump) {
                // Defer until the hop animation tick has finished.
                const totalAnimMs = (movePayload.path.length + 1) * 350 + 50;
                setTimeout(evalAndShow, totalAnimMs);
            } else {
                evalAndShow();
            }
        }

        return true;
    }, [buildGameStateFromEngine, computeLocalStats, config]);

    const localInitialBoardTextRef = useRef(null);

    // Initialize local game engine on mount (with optional restore from localStorage).
    // React.StrictMode (dev) double-invokes useEffect: Mount-1 runs, then a
    // simulated unmount + Mount-2 runs. Without cancellation, Mount-1's
    // pending fetch resolves against an engine that engineRef has already
    // moved on from (Mount-2 replaced it), so the LLM/UI see an empty board
    // even though puzzleMetadata is set. The `cancelled` flag in the cleanup
    // makes Mount-1's fetch.then a no-op once Mount-2 takes over.
    useEffect(() => {
        if (!isLocalGame) return;

        let cancelled = false;

        const engine = new PhutballEngine(configRows, configCols);
        localInitialBoardTextRef.current = engine.textRender(1);

        let saved = null;

        // Puzzle mode: fetch a curriculum-generated state from the server,
        // load it into the engine, and capture canonical-solution metadata.
        // Skip localStorage restore for puzzle mode — every puzzle is a
        // fresh seeded position. Endless mode pulls from the queue hook in
        // a separate effect below, so it skips the single-puzzle fetch here.
        if (config?.gameMode === 'puzzle' && config?.puzzleAttemptMode !== 'endless') {
            const puzzleCfg = config?.puzzle || {};
            puzzleFetchInFlightRef.current = true;
            setPuzzleFetching(true);
            // Resolve from the static catalog shipped under /puzzles/ instead
                // of a live /puzzle?... call. The Cloudflare Worker that powers
                // the hosted site cannot run the JAX generators, so puzzles are
                // precomputed offline by scripts/generate_puzzle_catalog.py and
                // surfaced through fetchPuzzleFromCatalog.
            fetchPuzzleFromCatalog({
                puzzle_type: puzzleCfg.puzzle_type ?? 'win',
                num_jumps: puzzleCfg.num_jumps ?? 1,
                player: puzzleCfg.player ?? 1,
                seed: puzzleCfg.seed,
            })
                .then(data => {
                    if (cancelled) return;
                    lastPuzzleDataRef.current = data;
                    engine.loadSnapshot({
                        board: data.board,
                        currentPlayer: data.currentPlayer,
                        phutballPos: data.phutballPos,
                        numTurns: 0,
                        winner: 0,
                        jumpSeq: [],
                        jumpVisitCounts: {},
                    });
                    // Capture the puzzle board as the canonical "Game Start"
                    // text so subsequent processLocalMove rebuilds (e.g. when
                    // the LLM commits its move) don't revert the LogPanel's
                    // initial-board entry to the empty starting position.
                    const puzzleInitialText = engine.textRender(data.currentPlayer);
                    localInitialBoardTextRef.current = puzzleInitialText;
                    localHistoryRef.current = ['Game Start'];
                    localLogHistoryRef.current = [];
                    setPuzzleMetadata(data.puzzle || null);
                    setPuzzleEvalResult(null);
                    puzzleAttemptCommittedRef.current = false;
                    // Rebuild the visible game state from the now-seeded engine
                    setGameState(buildGameStateFromEngine(engine, ['Game Start'], [], puzzleInitialText));
                    setCursorPos(data.phutballPos);
                })
                .catch(err => {
                    console.error('[Puzzle] fetch failed:', err);
                })
                .finally(() => {
                    if (cancelled) return;
                    puzzleFetchInFlightRef.current = false;
                    setPuzzleFetching(false);
                });
            // Fall through so the rest of the init runs against the empty
            // engine; the fetch resolution will overwrite gameState above.
        } else {
            // Try to restore saved game (skipped in puzzle mode)
            saved = loadLocalGame(config);
            if (saved) {
                const result = engine.replayMoves(saved.moves);
                if (!result.success) {
                    clearLocalGame();
                    engine.reset();
                }
            }
        }

        engineRef.current = engine;
        const moveHistory = engine.getMoveHistory();
        localMovesRef.current = saved?.moves && moveHistory.length > 0 ? [...saved.moves] : [];

        // Reset telemetry trackers for a fresh game (or a restored game continuation)
        gameStartAtRef.current = Date.now();
        gameTokenTotalsRef.current = null;
        gameCompleteSentRef.current = false;

        // Build tree root
        const rootNode = createTreeNode(-1, 'Game Start', 1, 1, null, false, false, 0, 0);
        localTreeRootRef.current = rootNode;
        localTreeUidRef.current = 0;
        const snapshots = new Map();
        localNodeSnapshotsRef.current = snapshots;

        // Rebuild tree, history, log, and snapshots from move history
        const history = ['Game Start'];
        const logHistory = [];
        const linearSnapshots = [engine.getMoveCount() === 0 ? engine.getSnapshot() : null]; // placeholder
        const statsHistory = [];

        if (moveHistory.length > 0) {
            // Re-create a replay engine to step through moves and build tree
            const replayEngine = new PhutballEngine(configRows, configCols);
            snapshots.set(rootNode.id, replayEngine.getSnapshot());
            linearSnapshots[0] = replayEngine.getSnapshot();

            let parentNode = rootNode;
            let turnIndex = 0;
            let prevPlayer = 1;

            // engine.getMoveHistory() carries only { type, path, player } per
            // entry, but the saved-game payload also carries the LLM trace
            // (reasoning / thinkingTime / tokenUsage). Index-pair them so the
            // tree rebuild populates the LogPanel with the original trace
            // instead of leaving each action's reasoning null on reload.
            const savedMoves = Array.isArray(saved?.moves) ? saved.moves : [];
            for (let moveIdx = 0; moveIdx < moveHistory.length; moveIdx++) {
                const move = moveHistory[moveIdx];
                const savedMeta = savedMoves[moveIdx] || {};
                const playerBefore = replayEngine.currentPlayer;
                const phutballPosBefore = [...replayEngine.phutballPos];

                // Detect turn boundaries (new turn when player changes from previous move's player)
                if (playerBefore !== prevPlayer || turnIndex === 0) {
                    turnIndex++;
                }

                if (move.type === 'placement') {
                    const placeResult = replayEngine.applyPlacement(move.path[0]);
                    if (!placeResult.success) break;
                    const [r, c] = move.path[0];
                    const nodeDesc = `P${playerBefore}: Place at (${r}, ${c})`;
                    const uid = ++localTreeUidRef.current;
                    const node = createTreeNode(uid, nodeDesc, playerBefore, replayEngine.currentPlayer, parentNode.id, false, false, turnIndex, 0);
                    parentNode.children.push(node);
                    snapshots.set(node.id, replayEngine.getSnapshot());
                    parentNode = node;
                } else if (move.type === 'jump' && move.path.length > 0) {
                    // Expand multi-hop jumps into one tree node per hop +
                    // a terminal halt node, matching processLocalMove for
                    // live play. Without this, reloading collapsed every
                    // multi-hop jump into a single tree node and lost the
                    // ability to branch at intermediate hops.
                    let prev = phutballPosBefore;
                    let terminated = false;
                    for (let hopIdx = 0; hopIdx < move.path.length; hopIdx += 1) {
                        const target = move.path[hopIdx];
                        const hopResult = replayEngine.applyJump(target);
                        if (!hopResult.success) { terminated = true; break; }
                        const hopDesc = `P${playerBefore}: Jump (${prev[0]}, ${prev[1]}) -> (${target[0]}, ${target[1]})`;
                        const hopUid = ++localTreeUidRef.current;
                        const hopNode = createTreeNode(hopUid, hopDesc, playerBefore, playerBefore, parentNode.id, true, false, turnIndex, 0);
                        parentNode.children.push(hopNode);
                        snapshots.set(hopNode.id, replayEngine.getSnapshot());
                        parentNode = hopNode;
                        prev = target;
                        if (hopResult.terminated) { terminated = true; break; }
                    }
                    if (!terminated && replayEngine.winner === 0) {
                        const haltResult = replayEngine.applyHalt();
                        if (haltResult.success) {
                            const haltDesc = `P${playerBefore}: Halt jump sequence`;
                            const haltUid = ++localTreeUidRef.current;
                            const haltNode = createTreeNode(haltUid, haltDesc, playerBefore, replayEngine.currentPlayer, parentNode.id, false, true, turnIndex, 0);
                            parentNode.children.push(haltNode);
                            snapshots.set(haltNode.id, replayEngine.getSnapshot());
                            parentNode = haltNode;
                        }
                    }
                } else {
                    const haltResult = replayEngine.applyHalt();
                    if (!haltResult.success) break;
                    const nodeDesc = `P${playerBefore}: Halt jump sequence`;
                    const uid = ++localTreeUidRef.current;
                    const node = createTreeNode(uid, nodeDesc, playerBefore, replayEngine.currentPlayer, parentNode.id, false, true, turnIndex, 0);
                    parentNode.children.push(node);
                    snapshots.set(node.id, replayEngine.getSnapshot());
                    parentNode = node;
                }

                // Every saved move is a complete turn (applyMove includes implicit halt)
                const moveNum = history.length;
                let desc;
                if (move.type === 'placement') {
                    const [r, c] = move.path[0];
                    desc = `${moveNum}. P${playerBefore}: Place at (${r}, ${c})`;
                } else {
                    const allPositions = [phutballPosBefore, ...move.path];
                    const pathStr = allPositions.map(([r, c]) => `(${r}, ${c})`).join(' -> ');
                    desc = `${moveNum}. P${playerBefore}: Jump ${pathStr}`;
                }
                history.push(desc);

                const boardText = replayEngine.textRender(playerBefore);
                // Pull the LLM trace from the saved-game metadata (parallel
                // index to moveHistory). Older saves omit these fields, in
                // which case savedMeta is empty and we render blanks.
                logHistory.push({
                    turnIndex,
                    player: playerBefore,
                    actions: [{
                        moveDesc: desc,
                        boardText,
                        reasoning: savedMeta.reasoning ?? null,
                        thinkingTime: savedMeta.thinkingTime ?? null,
                        tokenUsage: savedMeta.tokenUsage ?? null,
                    }],
                    turnReasoning: null,
                });

                linearSnapshots.push(replayEngine.getSnapshot());
                {
                    const entry = computeLocalStats(replayEngine);
                    statsHistory.push(entry);
                    queueValueEval(entry, replayEngine.getSnapshot());
                }

                prevPlayer = playerBefore;
            }

            localTreeCurrentRef.current = parentNode;
        } else {
            snapshots.set(rootNode.id, engine.getSnapshot());
            localTreeCurrentRef.current = rootNode;
        }

        localHistoryRef.current = history;
        localLogHistoryRef.current = logHistory;
        localSnapshotsRef.current = linearSnapshots.filter(Boolean);
        localStatsHistoryRef.current = statsHistory;

        setStatus('Local Game');
        setGameState(buildGameStateFromEngine(engine, history, logHistory, localInitialBoardTextRef.current));

        return () => { cancelled = true; };
    }, [isLocalGame, configRows, configCols, buildGameStateFromEngine, config, computeLocalStats]);

    // Endless mode: load next puzzle from the queue whenever endlessNeedsNext
    // flips on. The queue keeps a per-type buffer pre-generated; first three
    // puzzles always serve fast types so the user never waits at puzzle 1.
    useEffect(() => {
        if (!isEndlessMode || !endlessNeedsNext) return;
        if (endlessRunSummary) return;  // run already ended
        // Pop returns null if no slot is ready yet; retry when queue depths change.
        const data = puzzleQueue.popNext();
        if (!data) return;
        // Build fresh engine seeded with the popped puzzle.
        const engine = new PhutballEngine(configRows, configCols);
        engineRef.current = engine;
        engine.loadSnapshot({
            board: data.board,
            currentPlayer: data.currentPlayer,
            phutballPos: data.phutballPos,
            numTurns: 0,
            winner: 0,
            jumpSeq: [],
            jumpVisitCounts: {},
        });
        const puzzleInitialText = engine.textRender(data.currentPlayer);
        localInitialBoardTextRef.current = puzzleInitialText;
        localHistoryRef.current = ['Game Start'];
        localLogHistoryRef.current = [];
        localSnapshotsRef.current = [engine.getSnapshot()];
        localStatsHistoryRef.current = [];

        const rootNode = createTreeNode(-1, 'Game Start', 1, 1, null, false, false, 0, 0);
        localTreeRootRef.current = rootNode;
        localTreeCurrentRef.current = rootNode;
        localTreeUidRef.current = 0;
        localNodeSnapshotsRef.current = new Map();
        localNodeSnapshotsRef.current.set(rootNode.id, engine.getSnapshot());

        setStagedMove({ type: 'none', path: [] });
        setJumpPreview(emptyJumpPreview);
        setInvalidPlacement(null);
        setPuzzleMetadata(data.puzzle || null);
        setPuzzleEvalResult(null);
        puzzleAttemptCommittedRef.current = false;
        setGameState(buildGameStateFromEngine(engine, ['Game Start'], [], puzzleInitialText));
        setCursorPos(data.phutballPos);

        // Stats: stamp the start time and type of the puzzle the user is now solving.
        if (endlessStatsRef.current) {
            endlessStatsRef.current.currentPuzzleStartedAt = Date.now();
            endlessStatsRef.current.currentPuzzleType = data._servedType || data.puzzle?.puzzle_type;
        }
        setEndlessNeedsNext(false);
    }, [isEndlessMode, endlessNeedsNext, endlessRunSummary, puzzleQueue, configRows, configCols,
        buildGameStateFromEngine]);

    // Endless mode: react to each puzzleEvalResult. Solved -> record time, ask
    // for the next puzzle. Failed -> end the run with a per-type summary.
    useEffect(() => {
        if (!isEndlessMode || !puzzleEvalResult) return;
        if (endlessRunSummary) return;
        const stats = endlessStatsRef.current;
        if (!stats) return;

        // Helper: build summary payload + (for capped runs only) save+display
        // highscore. Capped runs only count toward highscore if completed
        // clean (no mistake, hit the cap exactly).
        const finalize = ({ cleanFinish }) => {
            const totalSolved = Object.values(stats.byType).reduce((a, arr) => a + arr.length, 0);
            const totalMs = Date.now() - stats.startedAt;
            const perTypeAvg = {};
            for (const [type, times] of Object.entries(stats.byType)) {
                if (times.length > 0) {
                    perTypeAvg[type] = {
                        count: times.length,
                        avgMs: Math.round(times.reduce((a, b) => a + b, 0) / times.length),
                    };
                }
            }
            const cap = config?.endless?.cap ?? 0;
            const runLengthId = config?.endless?.runLengthId || 'endless';
            let highscore = null;
            let newBest = false;
            try {
                const raw = localStorage.getItem('phutball_endless_highscores');
                const scores = raw ? JSON.parse(raw) : {};
                if (cleanFinish && cap > 0) {
                    // Capped run completed: track fastest total time per run length.
                    const prev = scores[runLengthId]?.bestTotalMs;
                    if (prev == null || totalMs < prev) {
                        scores[runLengthId] = { bestTotalMs: totalMs };
                        newBest = true;
                    }
                    highscore = { bestTotalMs: scores[runLengthId].bestTotalMs };
                } else if (cap === 0) {
                    // Endless run: track max puzzles solved.
                    const prev = scores.endless?.maxSolved;
                    if (prev == null || totalSolved > prev) {
                        scores.endless = { maxSolved: totalSolved };
                        newBest = true;
                    }
                    highscore = { maxSolved: scores.endless.maxSolved };
                }
                localStorage.setItem('phutball_endless_highscores', JSON.stringify(scores));
            } catch (e) { /* localStorage unavailable; skip */ }

            setEndlessRunSummary({
                totalSolved,
                totalMs,
                perTypeAvg,
                failedOn: cleanFinish ? null : stats.currentPuzzleType,
                runLengthId,
                cap,
                cleanFinish,
                highscore,
                newBest,
            });
        };

        if (puzzleEvalResult.solved) {
            const elapsedMs = stats.currentPuzzleStartedAt ? Date.now() - stats.currentPuzzleStartedAt : 0;
            const t = stats.currentPuzzleType;
            if (t && stats.byType[t]) stats.byType[t].push(elapsedMs);
            stats.currentPuzzleStartedAt = null;
            stats.currentPuzzleType = null;
            const totalSolved = Object.values(stats.byType).reduce((a, arr) => a + arr.length, 0);
            const cap = config?.endless?.cap ?? 0;
            if (cap > 0 && totalSolved >= cap) {
                // Capped run completed clean. End with success.
                finalize({ cleanFinish: true });
                return;
            }
            // Otherwise advance to the next puzzle after a brief beat.
            setTimeout(() => {
                setEndlessNeedsNext(true);
            }, 600);
        } else {
            // First mistake ends the run.
            finalize({ cleanFinish: false });
        }
    }, [isEndlessMode, puzzleEvalResult, endlessRunSummary, config]);
    const isCurrentPlayerAI = useCallback(() => {
        // Use the engine's currentPlayer (post-move state, including the
        // halt that flips the turn) rather than gameState.currentPlayer.
        // During the multi-hop jump animation, gameState.currentPlayer is
        // intentionally held at the mover so the board doesn't rotate
        // mid-jump; reading from gameState here would make the AI fire
        // again for the same side the moment setLlmThinking(false) ran,
        // producing self-play in VS Computer mode.
        const engineCurrent = engineRef.current?.currentPlayer ?? gameState.currentPlayer;
        // Watch-an-LLM puzzle: only the puzzle-player slot acts as AI; the other
        // side is treated as Human (puzzles are single-turn so this only matters
        // through the one committed move).
        if (puzzleAIOverride) {
            // .responder for threat puzzles (the side actually making the move),
            // .player for everything else; falls back to the dial selection.
            const puzzlePlayer = puzzleMetadata?.responder
                ?? puzzleMetadata?.player
                ?? puzzleControls.player ?? 1;
            return engineCurrent === puzzlePlayer;
        }
        const playerKey = engineCurrent === 1 ? 'p1' : 'p2';
        const playerType = config?.[playerKey];
        return playerType && playerType !== 'Human';
    }, [config, gameState.currentPlayer, puzzleAIOverride, puzzleMetadata, puzzleControls.player]);

    // Get AI config for current player
    const getCurrentAIConfig = useCallback(() => {
        if (puzzleAIOverride) {
            // Puzzles ask for the whole jump chain in one response. In
            // 'single' mode the local move path (processLocalMove) halts
            // after the first hop, so a multi-jump solution was cut to one
            // jump and the turn ended. full_sequence matches the headless
            // harness (experiments/llm_puzzle_eval) and gives no legal-move
            // hints, so web and harness numbers are comparable.
            return {
                provider: puzzleAIOverride.provider,
                model: puzzleAIOverride.model,
                planMode: 'full_sequence',
            };
        }
        const playerKey = gameState.currentPlayer === 1 ? 'p1' : 'p2';
        const provider = config?.[playerKey];
        return {
            provider,
            model: config?.modelName,
            planMode: config?.llmPlanMode || 'single',
        };
    }, [config, gameState.currentPlayer, puzzleAIOverride]);

    // Eagerly initialize AlphaZero worker when either player is AlphaZero
    useEffect(() => {
        const isAZ = config?.p1 === 'AlphaZero' || config?.p2 === 'AlphaZero';
        if (!isAZ || !isLocalGame) return;
        if (azServiceRef.current?.isReady) return;

        let cancelled = false;
        (async () => {
            setAzLoading(true);
            try {
                const svc = new AlphaZeroService();
                await svc.init(configRows, configCols);
                if (!cancelled && !valueSvcRef.current?.isReady) {
                    const vs = new AlphaZeroService();
                    await vs.init(configRows, configCols);
                    valueSvcRef.current = vs;
                    // positions played before the evaluator was ready
                    localStatsHistoryRef.current.forEach(entry => {
                        if (entry.pendingSnap) { const sn = entry.pendingSnap; delete entry.pendingSnap; queueValueEval(entry, sn); }
                    });
                }
                if (!cancelled) {
                    azServiceRef.current = svc;
                    setAzLoading(false);
                    console.log('[App] AlphaZero model loaded');
                } else {
                    svc.terminate();
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('[App] AlphaZero init failed:', err);
                    setAzLoading(false);
                    setLlmError(`Failed to load AlphaZero model: ${err.message}`);
                }
            }
        })();
        return () => {
            cancelled = true;
            if (azServiceRef.current) {
                azServiceRef.current.terminate();
                azServiceRef.current = null;
            }
        };
    }, [config?.p1, config?.p2, configRows, configCols, isLocalGame]);

    // Trigger LLM/AlphaZero move when it's an AI player's turn in a local game
    useEffect(() => {
        if (!isLocalGame) return;
        if (!isAtLiveNode) return;
        if (gameState.winner !== 0) return;
        if (llmRequestPending.current) return;
        if (llmThinking) return;
        if (!isCurrentPlayerAI()) return;
        if (!engineRef.current) return;
        // The engine's winner is set synchronously; gameState.winner is held
        // at 0 during the multi-hop jump animation, so it cannot be trusted
        // here. Without this, a winning jump re-queried the model mid-animation.
        if (engineRef.current.winner !== 0) return;
        // Puzzle mode: hold the LLM until /puzzle resolves and the engine has
        // been overwritten with the puzzle snapshot. Without this guard, the
        // AI fires against the empty starting board and the LLM produces an
        // opening-style placement that has nothing to do with the puzzle.
        if (config?.gameMode === 'puzzle' && (puzzleFetching || !puzzleMetadata)) return;
        // Single-turn puzzle: once the LLM (or the human, in solo mode) has
        // committed a move and the evaluator has run, the attempt is over.
        // Don't re-fire requestLLMMove when the user navigates history nodes
        // or the opponent's turn rolls around.
        if (config?.gameMode === 'puzzle' && (puzzleEvalResult || puzzleAttemptCommittedRef.current)) return;

        const aiConfig = getCurrentAIConfig();

        if (aiConfig.provider === 'AlphaZero') {
            // Client-side AlphaZero via Web Worker
            const engine = engineRef.current;
            llmRequestPending.current = true;
            setLlmThinking(true);
            setLlmError(null);

            (async () => {
                try {
                    // Wait for model to finish loading
                    while (!azServiceRef.current?.isReady) {
                        await new Promise(r => setTimeout(r, 500));
                        if (gameState.winner !== 0) return;
                    }

                    let sims = 100;
                    const match = aiConfig.model?.match(/playouts-(\d+)/);
                    if (match) sims = parseInt(match[1], 10);

                    const result = await azServiceRef.current.selectFullMove(
                        {
                            board: engine.board.map(row => [...row]),
                            phutballPos: [...engine.phutballPos],
                            currentPlayer: engine.currentPlayer,
                            isJumping: engine.isJumpSeqUnderway(),
                            jumpSeq: engine.jumpSeq.map(p => [...p]),
                            jumpVisitCounts: { ...engine.jumpVisitCounts },
                            winner: engine.winner,
                        },
                        { simulations: sims, temperature: 0.1 }
                    );

                    if (result.move) {
                        const reasoning = `AlphaZero (${sims} sims, value=${result.value?.toFixed(3)})`;
                        processLocalMove(result.move, reasoning);
                    }
                } catch (err) {
                    console.error('[App] AlphaZero error:', err);
                    setLlmError(`AlphaZero error: ${err.message}`);
                } finally {
                    llmRequestPending.current = false;
                    setLlmThinking(false);
                }
            })();
            return;
        }

        const engine = engineRef.current;
        llmRequestPending.current = true;
        setLlmThinking(true);
        setLlmError(null);

        const MAX_RETRIES = 3;

        (async () => {
            let errorFeedback = null;

            for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
                const result = await requestLLMMove({
                    provider: aiConfig.provider,
                    model: aiConfig.model,
                    apiKey: puzzleAIOverride?.apiKey ?? config?.apiKey,
                    isPuzzle: !!puzzleAIOverride,
                    board: engine.board.map(row => [...row]),
                    currentPlayer: engine.currentPlayer,
                    rows: configRows,
                    cols: configCols,
                    turnNumber: engine.numTurns,
                    jumpSequenceActive: engine.isJumpSeqUnderway(),
                    legalJumpMoves: engine.getLegalJumps(),
                    canHalt: engine.isJumpSeqUnderway(),
                    planMode: aiConfig.planMode,
                    errorFeedback,
                    systemPromptOverride: config?.systemPromptOverride || null,
                });

                if (result.error && !result.move) {
                    // Stop retrying on transport-level failures (rate limit,
                    // auth, server error) that an immediate retry won't fix
                    // and that will just compound the issue by burning more
                    // quota. Retries are for "model returned an unparseable
                    // move" cases, where re-asking with errorFeedback can
                    // succeed.
                    if (/\b(401|403|429|5\d\d)\b/.test(result.error)
                        || /rate limit/i.test(result.error)) {
                        llmRequestPending.current = false;
                        setLlmThinking(false);
                        setLlmError(`AI request failed: ${result.error}`);
                        return;
                    }
                    errorFeedback = result.error;
                    continue;
                }

                // Accumulate tokens for game_complete telemetry
                if (result.usage) {
                    if (!gameTokenTotalsRef.current) {
                        gameTokenTotalsRef.current = { input: 0, output: 0, reasoning: 0, cached: 0, costUsd: 0 };
                    }
                    const acc = gameTokenTotalsRef.current;
                    acc.input += result.usage.input || 0;
                    acc.output += result.usage.output || 0;
                    acc.reasoning += result.usage.reasoning || 0;
                    acc.cached += result.usage.cached || 0;
                    acc.costUsd += result.usage.costUsd || 0;
                }

                const success = processLocalMove(result.move, result.reasoning, {
                    thinkingTime: result.thinkingTime,
                    tokenUsage: result.tokenUsage,
                });
                if (success) {
                    if (puzzleAIOverride) {
                        setPuzzleLLMStats({
                            provider: aiConfig.provider,
                            model: aiConfig.model,
                            usage: result.usage || null,
                            thinkingTime: result.thinkingTime ?? null,
                            attempts: attempt + 1,
                        });
                    }
                    llmRequestPending.current = false;
                    setLlmThinking(false);
                    setLlmError(null);
                    return;
                }

                errorFeedback = `Move rejected as illegal: ${JSON.stringify(result.move)}`;
            }

            llmRequestPending.current = false;
            setLlmThinking(false);
            setLlmError(`AI failed after ${MAX_RETRIES} attempts: ${errorFeedback}`);
        })();
    }, [isLocalGame, isAtLiveNode, gameState.currentPlayer, gameState.winner, llmThinking,
        isCurrentPlayerAI, getCurrentAIConfig, config, configRows, configCols, processLocalMove,
        puzzleAIOverride, puzzleFetching, puzzleMetadata, puzzleEvalResult]);

    const canAct = isAtLiveNode && !awaitingAck && !llmThinking && !isCurrentPlayerAI() && !isReplayingHistory;

    const handleRequestBranch = useCallback(() => {
        const currentId = gameState.historyTree?.currentNodeId;
        const liveId = gameState.historyTree?.liveNodeId;
        if (isReplayingHistory || !currentId || !liveId || currentId === liveId) {
            return false;
        }
        pendingMoveRef.current = null;
        selectedHistoryIndexRef.current = null;
        pendingTreeSelectionRef.current = null;
        setStagedMove({ type: 'none', path: [] });
        setJumpPreview(emptyJumpPreview);
        setInvalidPlacement(null);

        if (isLocalGame) {
            // Local branching: make the current historical node the new live node
            const currentNode = nodesById.get(currentId);
            const snapshot = localNodeSnapshotsRef.current.get(currentId);
            if (!currentNode || !snapshot) return false;

            // Load engine state from the historical node
            engineRef.current.loadSnapshot(snapshot);

            // Set tree current to this node (new moves will branch from here)
            localTreeCurrentRef.current = currentNode;

            // Rebuild linear history from root → current node
            const pathToNode = [];
            let walker = currentNode;
            const visited = new Set();
            while (walker && !visited.has(walker.id)) {
                pathToNode.unshift(walker);
                visited.add(walker.id);
                walker = walker.parentId ? nodesById.get(walker.parentId) : null;
            }
            const history = pathToNode.map(n => n.moveDesc ?? 'Game Start');
            localHistoryRef.current = history;

            // Rebuild linear snapshots from the path
            const linearSnapshots = pathToNode
                .map(n => localNodeSnapshotsRef.current.get(n.id))
                .filter(Boolean);
            localSnapshotsRef.current = linearSnapshots;

            // Rebuild log history from the path nodes (skip root)
            const logHistory = [];
            for (let i = 1; i < pathToNode.length; i++) {
                const n = pathToNode[i];
                const nodeSnapshot = localNodeSnapshotsRef.current.get(n.id);
                const boardText = nodeSnapshot
                    ? engineRef.current.textRender?.(n.player) ?? ''
                    : '';
                logHistory.push({
                    turnIndex: i,
                    player: n.player,
                    actions: [{ moveDesc: n.moveDesc, boardText, reasoning: null }],
                    turnReasoning: null,
                });
            }
            localLogHistoryRef.current = logHistory;

            // Rebuild stats history from branch path snapshots
            const branchStats = [];
            for (let i = 1; i < pathToNode.length; i++) {
                const nodeSnap = localNodeSnapshotsRef.current.get(pathToNode[i].id);
                if (nodeSnap) {
                    // Compute stats from snapshot board state
                    const ballRow = nodeSnap.phutballPos[0];
                    const midRow = Math.floor((nodeSnap.board.length) / 2);
                    const fp = midRow > 0 ? (ballRow - midRow) / midRow : 0;
                    let above = 0, below = 0;
                    for (let r = 0; r < nodeSnap.board.length; r++) {
                        for (let c = 0; c < nodeSnap.board[r].length; c++) {
                            if (nodeSnap.board[r][c] === 1) {
                                if (r < ballRow) above++;
                                else if (r > ballRow) below++;
                            }
                        }
                    }
                    const total = above + below;
                    branchStats.push({
                        fieldPosition: fp,
                        pieceTilt: total > 0 ? (below - above) / total : 0,
                        value: 0,
                    });
                }
            }
            localStatsHistoryRef.current = branchStats;

            // Compute pieceTilt for branch point
            let branchPieceTilt = null;
            {
                const bBallRow = snapshot.phutballPos[0];
                let bAbove = 0, bBelow = 0;
                for (let r = 0; r < snapshot.board.length; r++) {
                    for (let c = 0; c < snapshot.board[r].length; c++) {
                        if (snapshot.board[r][c] === 1) {
                            if (r < bBallRow) bAbove++;
                            else if (r > bBallRow) bBelow++;
                        }
                    }
                }
                const bTotal = bAbove + bBelow;
                if (bTotal > 0) {
                    branchPieceTilt = {
                        p1: Math.round(100 * bAbove / bTotal),
                        p2: Math.round(100 * bBelow / bTotal),
                    };
                }
            }

            // Update game state: current node becomes the live node
            setGameState(prev => ({
                ...prev,
                board: snapshot.board.map(row => [...row]),
                currentPlayer: snapshot.currentPlayer,
                winner: snapshot.winner,
                phutballPos: [...snapshot.phutballPos],
                history: [...history],
                historyIndex: history.length,
                logHistory: [...logHistory],
                pieceTilt: branchPieceTilt,
                statsHistory: [...branchStats],
                historyTree: {
                    ...prev.historyTree,
                    currentNodeId: currentId,
                    liveNodeId: currentId,
                },
            }));

            // Clear perspective so turn-mode perspective applies
            setPreviewPerspectivePlayer(null);
            setHistoryBranchMode(false);
            setIsInTurnMode(true);
            return true;
        }

        setHistoryBranchMode(true);
        clearStageOnNextStateRef.current = true;
        sendAction({ action: 'branch_from_current' });
        setHistoryFocusIndex(typeof gameState.historyIndex === 'number' ? gameState.historyIndex : 0);
        return true;
    }, [gameState.historyTree, gameState.historyIndex, isReplayingHistory, sendAction, setStagedMove, setJumpPreview, setInvalidPlacement, setHistoryFocusIndex, isLocalGame, nodesById]);

    const cancelHistoryHoverPreview = useCallback(() => {
        const { active, index } = historyHoverPreviewRef.current ?? {};
        const hadPreview = Boolean(active) || (index != null);
        historyHoverPreviewRef.current = { active: false, index: null, source: null };
        previewRequestRef.current = { source: null, historyIndex: null, parentNodeId: null, nodeId: null };
        if (!hadPreview) {
            return false;
        }
        setHistoryPreviewState(null);
        setPreviewPerspectivePlayer(null);
        sendAction({ action: 'preview_history_entry', index: null });
        return true;
    }, [sendAction]);

    useEffect(() => () => {
        cancelHistoryHoverPreview();
    }, [cancelHistoryHoverPreview]);

    useEffect(() => {
        if (historyView !== 'linear') {
            cancelHistoryHoverPreview();
        }
    }, [historyView, cancelHistoryHoverPreview]);

    useEffect(() => {
        if (isReplayingHistory) {
            cancelHistoryHoverPreview();
        }
    }, [isReplayingHistory, cancelHistoryHoverPreview]);

    // Restore live state for local games (shared helper)
    const goLiveLocal = useCallback(() => {
        const latestIdx = localSnapshotsRef.current.length - 1;
        const liveSnapshot = localSnapshotsRef.current[latestIdx];
        if (!liveSnapshot) return;
        engineRef.current.loadSnapshot(liveSnapshot);
        const liveId = gameState.historyTree?.liveNodeId;
        if (liveId) localTreeCurrentRef.current = nodesById.get(liveId) ?? localTreeCurrentRef.current;
        setGameState(prev => ({
            ...prev,
            board: liveSnapshot.board.map(row => [...row]),
            currentPlayer: liveSnapshot.currentPlayer,
            winner: liveSnapshot.winner,
            phutballPos: [...liveSnapshot.phutballPos],
            historyTree: { ...prev.historyTree, currentNodeId: prev.historyTree?.liveNodeId ?? prev.historyTree?.currentNodeId },
        }));
    }, [gameState.historyTree?.liveNodeId, nodesById]);

    const handlePlace = useCallback(() => {
        if (!isAtLiveNode) return;
        if (llmThinking || isCurrentPlayerAI()) return;
        if (awaitingAck) {
            console.warn('Cancelling pending submission');
            pendingMoveRef.current = null;
            setAwaitingAck(false);
        }
        selectedHistoryIndexRef.current = null;
        const { board } = gameState;
        const fallback = gameState.phutballPos ?? [0, 0];
        const pos = Array.isArray(cursorPos) ? cursorPos : fallback;
        if (!isLegalPlacement(board, pos)) {
            setInvalidPlacement(pos);
            return;
        }
        setStagedMove({
            type: 'placement',
            path: [pos],
            playerOverride: nodeToMove ?? undefined,
        });
        setJumpPreview(emptyJumpPreview);
        setInvalidPlacement(null);
        pendingMoveRef.current = null;
        setAwaitingAck(false);
    }, [isAtLiveNode, llmThinking, isCurrentPlayerAI, awaitingAck, gameState, cursorPos, nodeToMove]);

    const handleJump = useCallback(() => {
        if (!isAtLiveNode) return;
        if (llmThinking || isCurrentPlayerAI()) return;
        if (awaitingAck) return;
        selectedHistoryIndexRef.current = null;
        if (Array.isArray(cursorPos)) {
            const { board, phutballPos } = gameState;
            setStagedMove(prev => {
                const basePath = prev.type === 'jump' ? prev.path : [];
                const newPath = [...basePath, cursorPos];
                const preview = evaluateJumpSequence(board, phutballPos, newPath);
                setJumpPreview(preview);
                setInvalidPlacement(null);
                return {
                    type: 'jump',
                    path: newPath,
                    startPos: prev.type === 'jump' && Array.isArray(prev.startPos)
                        ? prev.startPos
                        : (Array.isArray(phutballPos) ? [...phutballPos] : null),
                    playerOverride: prev.type === 'jump' && typeof prev.playerOverride === 'number'
                        ? prev.playerOverride
                        : (nodeToMove ?? undefined),
                };
            });
        }
    }, [isAtLiveNode, llmThinking, isCurrentPlayerAI, awaitingAck, cursorPos, gameState, nodeToMove]);

    const handleUndo = useCallback(() => {
        if (!isAtLiveNode) return;
        if (awaitingAck) {
            console.warn('Cancelling pending submission');
            pendingMoveRef.current = null;
            setAwaitingAck(false);
        }
        selectedHistoryIndexRef.current = null;
        const { board, phutballPos } = gameState;
        setStagedMove(prev => {
            if (prev.path.length > 0) {
                const nextPath = prev.path.slice(0, -1);
                if (prev.type === 'jump' && nextPath.length === 0) {
                    // Undid entire jump sequence — return to neutral
                    setJumpPreview(emptyJumpPreview);
                    setInvalidPlacement(null);
                    return { type: 'none', path: [] };
                }
                if (prev.type === 'jump') {
                    setJumpPreview(evaluateJumpSequence(board, phutballPos, nextPath));
                } else {
                    setJumpPreview(emptyJumpPreview);
                    setInvalidPlacement(null);
                }
                return { ...prev, path: nextPath };
            }
            return prev;
        });
    }, [isAtLiveNode, awaitingAck, gameState]);

    const handleConfirm = useCallback(() => {
        const canMoveInCurrentView = historyView === 'linear' || isInTurnMode;
        if (awaitingAck || !isAtLiveNode || !canMoveInCurrentView || llmThinking || isCurrentPlayerAI()) {
            return;
        }
        if (stagedMove.type !== 'none') {
            const { board } = gameState;
            const isHaltAttempt = stagedMove.type === 'jump' && stagedMove.path.length === 0;
            if (!isHaltAttempt && stagedMove.path.length === 0) {
                return;
            }

            if (stagedMove.type === 'placement') {
                if (!isLegalPlacement(board, stagedMove.path[0])) {
                    setInvalidPlacement(stagedMove.path[0]);
                    return;
                }
            } else if (stagedMove.type === 'jump' && !isHaltAttempt && jumpPreview.invalidIndex >= 0) {
                return;
            }

            const movePayload = isHaltAttempt
                ? { ...stagedMove, path: [] }
                : { ...stagedMove, path: [...stagedMove.path] };

            selectedHistoryIndexRef.current = null;
            setInvalidPlacement(null);
            setPreviewPerspectivePlayer(null);
            setIsInTurnMode(isAtLiveNode);

            if (isLocalGame) {
                // processLocalMove handles staged-move clearing itself,
                // including the trace-eating animation for multi-hop jumps.
                processLocalMove(movePayload);
            } else {
                const snapshot = JSON.parse(JSON.stringify(movePayload));
                pendingMoveRef.current = snapshot;
                setAwaitingAck(true);
                const sent = sendAction({ action: 'commit_move', move: movePayload });
                if (!sent) {
                    setAwaitingAck(false);
                    pendingMoveRef.current = null;
                }
            }
        }
    }, [awaitingAck, isAtLiveNode, historyView, isInTurnMode, llmThinking, isCurrentPlayerAI, stagedMove, gameState, jumpPreview, isLocalGame, processLocalMove, sendAction]);

    useEffect(() => {
        const historyPanelVisible = !isStackedLayout || PANEL_CONFIG[activePanelIndex]?.id === 'history';
        const boardPanelVisible = !isStackedLayout || PANEL_CONFIG[activePanelIndex]?.id === 'board';

        const handleKeyDown = (e) => {
            // Don't intercept when typing in an input or textarea
            const activeTag = document.activeElement?.tagName;
            if (activeTag === 'INPUT' || activeTag === 'TEXTAREA') {
                if (e.key === 'Escape') { document.activeElement.blur(); e.preventDefault(); }
                return;
            }
            if (isReplayingHistory) {
                if (e.key.toLowerCase() === 'r') {
                    // Let the history panel handle replay toggling
                    return;
                }
                return;
            }
            if (e.defaultPrevented) {
                return;
            }
            const key = e.key;
            const lower = key.toLowerCase();
            const isShifted = e.shiftKey;
            const wantsHistory = isShifted && (key === 'ArrowRight' || lower === 'd');
            const wantsBoard = isShifted && (key === 'ArrowLeft' || lower === 'a');

            const entries = Array.isArray(gameState.history) ? gameState.history : [];
            const entryCount = entries.length;
            const currentId = gameState.historyTree?.currentNodeId;
            const liveId = gameState.historyTree?.liveNodeId;
            const atLiveNode = currentId != null && liveId != null && currentId === liveId;
            const implicitLiveFocus = selectedHistoryIndexRef.current == null && atLiveNode;
            if (wantsHistory) {
                if (!historyPanelVisible) {
                    return;
                }
                setKeyFocus('history');
                if (isStackedLayout) { setActivePanelIndex(2); flashPanel(2); }
                const defaultIdx = typeof gameState.historyIndex === 'number'
                    ? Math.max(0, Math.min(gameState.historyIndex, entryCount ? entryCount - 1 : 0))
                    : 0;
                setHistoryFocusIndex(atLiveNode ? entryCount : defaultIdx);
                e.preventDefault();
                return;
            }
            if (wantsBoard) {
                if (!boardPanelVisible) {
                    return;
                }
                // Snap history back to the live node when escaping the
                // history panel, so we don't leave the user reviewing a
                // mid-history snapshot when they switch back to the board
                // to act. Only does anything when keyboard focus was in
                // history — direct shift+left from the board is a no-op.
                if (keyFocus === 'history' && entryCount > 0 && !atLiveNode) {
                    handleHistoryClick(entryCount);
                }
                setKeyFocus('board');
                if (isStackedLayout) { setActivePanelIndex(1); flashPanel(1); }
                e.preventDefault();
                return;
            }
            const { board, currentPlayer, phutballPos } = gameState;
            if (!board || board.length === 0) return;

            if (keyFocus === 'history') {
                if (!historyPanelVisible) {
                    if (boardPanelVisible) {
                        setKeyFocus('board');
                    }
                    return;
                }
                if (!entryCount) return;
                const liveRowActive = implicitLiveFocus || historyFocusIndex >= entryCount;
                if (key === 'Enter') {
                    e.preventDefault();
                    if (historyFocusIndex < entryCount) {
                        const toggleHandler = toggleJumpEntryRef.current;
                        if (typeof toggleHandler === 'function') {
                            toggleHandler(historyFocusIndex);
                        }
                    }
                    handleHistoryClick(historyFocusIndex);
                    return;
                }
                if (key === 'ArrowUp' || lower === 'w') {
                    e.preventDefault();
                    if (liveRowActive) {
                        const prevIndex = entryCount ? entryCount - 1 : 0;
                        setHistoryFocusIndex(prevIndex);
                        handleHistoryClick(prevIndex);
                    } else {
                        const target = advanceToTurn(entries, turnStartIndices, historyFocusIndex, -1);
                        const maxIdx = entryCount ? entryCount - 1 : 0;
                        const clamped = Math.max(0, Math.min(target, maxIdx));
                        if (clamped !== historyFocusIndex) {
                            setHistoryFocusIndex(clamped);
                            handleHistoryClick(clamped);
                        }
                    }
                    return;
                }
                if (key === 'ArrowDown' || lower === 's') {
                    e.preventDefault();
                    const liveIndex = entryCount;
                    // From the last linear entry or beyond, Down lands on
                    // the live row regardless of liveRowActive — covers the
                    // case where focus reached the last entry via tree
                    // selection or history scrub without setting the live
                    // implicit-focus flag, which previously left the user
                    // stuck one step short of the live row.
                    if (historyFocusIndex >= entryCount - 1) {
                        if (historyFocusIndex !== liveIndex) {
                            setHistoryFocusIndex(liveIndex);
                        }
                        handleHistoryClick(liveIndex);
                        return;
                    }
                    const target = advanceToTurn(entries, turnStartIndices, historyFocusIndex, 1);
                    const maxIdx = entryCount ? entryCount - 1 : 0;
                    const clamped = Math.max(0, Math.min(target, maxIdx));
                    if (clamped !== historyFocusIndex) {
                        setHistoryFocusIndex(clamped);
                        handleHistoryClick(clamped);
                    }
                    return;
                }
                if (lower === 'b') {
                    e.preventDefault();
                    const didBranch = handleRequestBranch();
                    if (didBranch) {
                        return;
                    }
                    return;
                }
                if (lower === 'l') {
                    e.preventDefault();
                    if (isLocalGame) {
                        goLiveLocal();
                    } else {
                        sendAction({ action: 'go_live_node' });
                    }
                    return;
                }
            }

            if (keyFocus !== 'board' || !boardPanelVisible) {
                return;
            }

            const moveCursor = (dr, dc) => {
                const effectiveDr = (effectivePerspective === 2) ? -dr : dr;
                const effectiveDc = (effectivePerspective === 2) ? -dc : dc;
                setCursorPos(prev => {
                    const numRows = gameState.board.length;
                    const numCols = gameState.board[0].length;
                    const base = prev ?? gameState.phutballPos ?? [0,0];
                    const newRow = Math.max(0, Math.min(numRows - 1, base[0] + effectiveDr));
                    const newCol = Math.max(0, Math.min(numCols - 1, base[1] + effectiveDc));
                    return [newRow, newCol];
                });
            };

            switch (e.key) {
                case 'Escape':
                    if (isPauseMenuOpen) {
                        // Pause menu handles its own Escape
                        break;
                    }
                    if (stagedMove.type !== 'none' || jumpPreview.isValid || invalidPlacement) {
                        setStagedMove({ type: 'none', path: [] });
                        setJumpPreview(emptyJumpPreview);
                        setInvalidPlacement(null);
                        pendingMoveRef.current = null;
                        setAwaitingAck(false);

                        if (isAtLiveNode) {
                             setPreviewPerspectivePlayer(null);
                        }
                    } else {
                        // Nothing to cancel - open the game menu (pause menu, or
                        // the puzzle-complete modal once the attempt is judged)
                        openGameMenuRef.current();
                    }
                    break;
                
                case 'w': case 'ArrowUp': moveCursor(-1, 0); break;
                case 's': case 'ArrowDown': moveCursor(1, 0); break;
                case 'a': case 'ArrowLeft': moveCursor(0, -1); break;
                case 'd': case 'ArrowRight': moveCursor(0, 1); break;

                // Move Staging
                case 'p':
                    handlePlace();
                    break;
                case 'j':
                    handleJump();
                    break;
                case 'Backspace':
                    handleUndo();
                    break;
                case 'Tab':
                    if (!atLiveNode) return;
                    if (awaitingAck) {
                        console.warn('Cancelling pending submission');
                        pendingMoveRef.current = null;
                        setAwaitingAck(false);
                    }
                    selectedHistoryIndexRef.current = null;
                    e.preventDefault();
                    setStagedMove({ type: 'none', path: [] });
                    setJumpPreview(emptyJumpPreview);
                    setInvalidPlacement(null);
                    pendingMoveRef.current = null;
                    setAwaitingAck(false);
                    break;
                case 'Enter':
                    if (e.shiftKey) {
                        handleCommitHalt();
                        return;
                    }
                    handleConfirm();
                    break;
                case 'l':
                case 'L':
                    if (isLocalGame) {
                        goLiveLocal();
                    } else {
                        sendAction({ action: 'go_live_node' });
                    }
                    break;
                case 'b':
                case 'B':
                    {
                        const currentId = gameState.historyTree?.currentNodeId;
                        const liveId = gameState.historyTree?.liveNodeId;
                        if (!isReplayingHistory && currentId && liveId && currentId !== liveId) {
                            const didBranch = handleRequestBranch();
                            if (didBranch) {
                                e.preventDefault();
                                return;
                            }
                        }
                    }
                    break;
                default: break;
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [gameState, stagedMove, cursorPos, jumpPreview, awaitingAck, sendAction, isReplayingHistory, keyFocus, historyFocusIndex, turnStartIndices, handleRequestBranch, isStackedLayout, activePanelIndex, nodeToMove, isLocalGame, processLocalMove, goLiveLocal, llmThinking, isCurrentPlayerAI, historyView, isInTurnMode, handlePlace, handleJump, handleUndo, handleConfirm]);

    const handleSelectTreeNode = useCallback((nodeId, options = {}) => {
        if (!nodeId) return;
        cancelHistoryHoverPreview();
        const { highlight = true, viaReplay = false } = options;
        setHistoryBranchMode(false);
        setIsInTurnMode(false);
        selectedHistoryIndexRef.current = null;
        pendingTreeSelectionRef.current = { nodeId, highlight };

        // During replay, don't flip perspective per-step (prevents jarring board flips)
        if (!viaReplay) {
            const node = nodesById.get(nodeId);
            if (node) {
                const moverPerspective = getNodePlayer(node);
                setPreviewPerspectivePlayer(moverPerspective ?? null);
            }
        }

        if (isLocalGame) {
            const snapshot = localNodeSnapshotsRef.current.get(nodeId);
            // Clear staged moves and any active tree preview (prevents stale parent-board from overriding)
            setStagedMove({ type: 'none', path: [] });
            setJumpPreview(emptyJumpPreview);
            setInvalidPlacement(null);
            setHistoryPreviewState(null);
            if (snapshot) {
                // Load snapshot into engine
                engineRef.current.loadSnapshot(snapshot);
                setGameState(prev => ({
                    ...prev,
                    board: snapshot.board.map(row => [...row]),
                    currentPlayer: snapshot.currentPlayer,
                    winner: snapshot.winner,
                    phutballPos: [...snapshot.phutballPos],
                    historyTree: {
                        ...prev.historyTree,
                        currentNodeId: nodeId,
                    },
                }));
            } else {
                // Snapshot missing - still advance currentNodeId so tree UI tracks properly
                console.warn('[local] No snapshot for node', nodeId);
                setGameState(prev => ({
                    ...prev,
                    historyTree: {
                        ...prev.historyTree,
                        currentNodeId: nodeId,
                    },
                }));
            }
            return;
        }

        sendAction({ action: 'select_tree_node', nodeId });
    }, [cancelHistoryHoverPreview, sendAction, nodesById, getNodePlayer, isLocalGame]);

    const lastPreviewEdgeRef = useRef(null);
    const handlePreviewTreeEdge = useCallback((edge) => {
        const key = edge ? `${edge.from.id}-${edge.to.id}` : null;
        if (lastPreviewEdgeRef.current === key) return;
        cancelHistoryHoverPreview();
        lastPreviewEdgeRef.current = key;
        if (edge) {
            const childNode = nodesById.get(edge.to.id);
            const player = getNodePlayer(childNode);
            setPreviewPerspectivePlayer(player ?? null);
            previewRequestRef.current = { source: 'tree', parentNodeId: edge.from.id, nodeId: edge.to.id };
            updatePreviewedTreeEdge({ parentId: edge.from.id, childId: edge.to.id });

            if (isLocalGame) {
                if (childNode) {
                    let preview = null;
                    // Atomic preview: snapshot = parent's state (so the ball
                    // sits at the previous hop's destination, and a single
                    // hover-to-hover transition glides it by one hop in the
                    // actual jump direction). The trace itself is the full
                    // chain from the sequence's pre-start through every hop
                    // up to and including the hovered one, so visited cells
                    // earlier in the sequence still render as dots.
                    let previewSnapshotId = edge.from.id;

                    const placement = parsePlacementEntry(childNode.moveDesc);
                    if (placement) {
                        preview = { type: 'placement', path: [placement.position] };
                    } else if (childNode.isJump) {
                        // Walk back through jump ancestors to collect every
                        // hop in this sequence up to the hovered one, plus
                        // the pre-sequence anchor for `start`.
                        const jumpNodes = [];
                        let cur = childNode;
                        while (cur && (cur.isJump || cur.isHalt)) {
                            if (cur.isJump) jumpNodes.unshift(cur);
                            cur = cur.parentId ? nodesById.get(cur.parentId) : null;
                        }
                        if (jumpNodes.length > 0) {
                            const firstJump = parseJumpEntry(jumpNodes[0].moveDesc);
                            const start = firstJump?.start ?? null;
                            const path = [];
                            for (const jn of jumpNodes) {
                                const parsed = parseJumpEntry(jn.moveDesc);
                                if (parsed?.path) path.push(...parsed.path);
                            }
                            if (start && path.length > 0) {
                                preview = { type: 'jump', path, start };
                            }
                        }
                    }
                    // Halt nodes have no movement to preview — fall through
                    // with preview = null and the parent's snapshot, which
                    // shows the post-final-hop state about to be committed.

                    const snapshot = localNodeSnapshotsRef.current.get(previewSnapshotId);
                    if (snapshot) {
                        handleHistoryPreviewMessage({
                            active: true,
                            source: 'tree',
                            board: snapshot.board.map(row => [...row]),
                            phutballPos: [...snapshot.phutballPos],
                            currentPlayer: snapshot.currentPlayer,
                            nodeId: edge.to.id,
                            parentNodeId: edge.from.id,
                            preview,
                        });
                    }
                }
            } else {
                sendAction({ action: 'preview_tree_edge', edge: { fromId: edge.from.id, toId: edge.to.id } });
            }
        } else {
            setPreviewPerspectivePlayer(null);
            previewRequestRef.current = { source: null, historyIndex: null, parentNodeId: null, nodeId: null };
            updatePreviewedTreeEdge({ parentId: null, childId: null });
            if (isLocalGame) {
                handleHistoryPreviewMessage({ active: false, source: 'tree' });
            } else {
                sendAction({ action: 'preview_tree_edge', edge: null });
            }
        }
    }, [cancelHistoryHoverPreview, sendAction, updatePreviewedTreeEdge, nodesById, getNodePlayer, isLocalGame, handleHistoryPreviewMessage]);

    // NN Visualization handlers
    const handleToggleVisualization = useCallback(async (nodeId) => {
        if (!config?.nnVisualization) return;

        if (visualizationNodeId === nodeId) {
            setShowModeSelectorForNode(showModeSelectorForNode === nodeId ? null : nodeId);
            return;
        }

        // Activate visualization for this node
        setVisualizationNodeId(nodeId);
        setVisualizationMode('placement-policy');
        setShowModeSelectorForNode(nodeId);

        // Get snapshot for this node
        const snapshot = localNodeSnapshotsRef.current.get(nodeId);
        if (!snapshot) return;

        const svc = azServiceRef.current;
        if (!svc?.isReady) {
            setNnDataStatus(prev => { const n = new Map(prev); n.set(nodeId, 'Failed'); return n; });
            return;
        }

        setNnDataStatus(prev => { const n = new Map(prev); n.set(nodeId, 'Processing'); return n; });

        try {
            const result = await svc.computeSaliency({
                board: snapshot.board,
                phutballPos: snapshot.phutballPos,
                currentPlayer: snapshot.currentPlayer,
                winner: snapshot.winner,
                isJumping: snapshot.jumpSeq?.length > 0,
                jumpSeq: snapshot.jumpSeq || [],
                jumpVisitCounts: snapshot.jumpVisitCounts || {},
            });

            setNnData({
                policyProbs: result.policyProbs,
                valueEstimate: result.valueEstimate,
                saliencyMap: [result.saliencyMap], // Wrap in array — single layer
            });
            setNnDataStatus(prev => { const n = new Map(prev); n.set(nodeId, 'Complete'); return n; });
        } catch (err) {
            console.warn('[NN Viz] Saliency computation failed:', err);
            setNnDataStatus(prev => { const n = new Map(prev); n.set(nodeId, 'Failed'); return n; });
        }
    }, [config?.nnVisualization, visualizationNodeId, showModeSelectorForNode]);

    const handleVisualizationModeChange = useCallback((mode) => {
        setVisualizationMode(mode);
        if (mode === 'off') {
            setVisualizationNodeId(null);
            setNnData(null);
            setShowModeSelectorForNode(null);
        }
        // Request NN data from backend if switching to a non-off mode
        // if (mode !== 'off' && visualizationNodeId) {
        //     sendAction({ action: 'fetch_nn_data', nodeId: visualizationNodeId });
        // }
    }, []);

    const handleCloseVisualizationModeSelector = useCallback(() => {
        setShowModeSelectorForNode(null);
    }, []);

    const handleCellClick = (row, col) => {
        // Block interaction during AI/opponent turn
        if (llmThinking || isCurrentPlayerAI()) {
            return;
        }
        if (historyPreviewState) {
            return;
        }
        const currentId = gameState.historyTree?.currentNodeId;
        const liveId = gameState.historyTree?.liveNodeId;
        const atLiveNode = Boolean(currentId && liveId && currentId === liveId);

        const clickedPos = [row, col];
        setCursorPos(clickedPos);
        selectedHistoryIndexRef.current = null;
        setKeyFocus('board');

        // Must be at live node AND in turn mode (for tree view) to make moves
        // Linear view allows moves when at live node; tree view requires explicit turn mode
        const canMoveInCurrentView = historyView === 'linear' || isInTurnMode;
        if (!atLiveNode || !canMoveInCurrentView) {
            return;
        }

        if (stagedMove.type === 'placement') {
            return;
        } else if (stagedMove.type === 'jump') {
            const nextPath = [...stagedMove.path, clickedPos];
            const startPos = Array.isArray(stagedMove.startPos)
                ? stagedMove.startPos
                : (Array.isArray(gameState.phutballPos) ? [...gameState.phutballPos] : null);
            setStagedMove({
                type: 'jump',
                path: nextPath,
                startPos,
                playerOverride: stagedMove.playerOverride ?? nodeToMove ?? undefined,
            });
            setJumpPreview(evaluateJumpSequence(gameState.board, gameState.phutballPos, nextPath));
            setInvalidPlacement(null);
        } else {
            if (!awaitingAck) {
                setInvalidPlacement(null);
                setStagedMove({ type: 'none', path: [] });
            }
        }
    };

    const applyHistoryEntryState = useCallback((entryIndex) => {
        const historyEntries = gameState.history ?? [];
        if (!Array.isArray(historyEntries) || historyEntries.length === 0) {
            setStagedMove({ type: 'none', path: [] });
            setJumpPreview(emptyJumpPreview);
            setInvalidPlacement(null);
            return null;
        }
        const bounded = Math.max(0, Math.min(entryIndex ?? 0, historyEntries.length - 1));
        const entry = historyEntries[bounded];
        if (!entry) {
            setStagedMove({ type: 'none', path: [] });
            setJumpPreview(emptyJumpPreview);
            setInvalidPlacement(null);
            return null;
        }
        const parsed = parseHistoryEntryForPreview(entry, {
            history: historyEntries,
            index: bounded,
        });
        const entryPlayerOverride = extractEntryPlayer(entry);
        const defaultPerspective = entryPlayerOverride ?? currentNodeMover ?? nodeLastMover ?? nodeToMove ?? null;
        const perspectivePlayer = bounded === 0 ? 1 : defaultPerspective;

        if (parsed?.type === 'jump' && parsed.path?.length) {
            setStagedMove({
                type: 'jump',
                path: parsed.path,
                startPos: parsed.start,
                playerOverride: entryPlayerOverride ?? undefined,
            });
            setJumpPreview(buildPreviewFromJumpPath(parsed.start, parsed.path));
            setInvalidPlacement(null);
            return perspectivePlayer;
        }

        if (parsed?.type === 'placement' && Array.isArray(parsed.position)) {
            setStagedMove({
                type: 'placement',
                path: [parsed.position],
                playerOverride: entryPlayerOverride ?? undefined,
            });
            setJumpPreview(emptyJumpPreview);
            setInvalidPlacement(null);
            return perspectivePlayer;
        }

        setStagedMove({ type: 'none', path: [] });
        setJumpPreview(emptyJumpPreview);
        setInvalidPlacement(null);
        return perspectivePlayer;
    }, [gameState.history, currentNodeMover, nodeLastMover, nodeToMove, setInvalidPlacement, setJumpPreview, setStagedMove]);

    const handleHistoryClick = (index) => {
        setHistoryBranchMode(false);
        cancelHistoryHoverPreview();
        setHistoryPreviewState(null);
        const historyEntries = gameState.history ?? [];
        const entryCount = historyEntries.length;
        const liveNodeId = gameState.historyTree?.liveNodeId ?? null;
        const currentNodeId = gameState.historyTree?.currentNodeId ?? null;
        const atLiveNode = liveNodeId != null && currentNodeId === liveNodeId;

        if (index >= entryCount) {
            pendingLiveFocusRef.current = true;
            const liveFocus = entryCount > 0 ? entryCount : 0;
            setHistoryFocusIndex(liveFocus);
            selectedHistoryIndexRef.current = null;
            pendingTreeSelectionRef.current = null;
            clearStageOnNextStateRef.current = true;
            setStagedMove({ type: 'none', path: [] });
            setJumpPreview(emptyJumpPreview);
            setInvalidPlacement(null);
            setPreviewPerspectivePlayer(livePerspectivePlayer ?? nodeToMove ?? null);

            if (!atLiveNode) {
                if (isLocalGame) {
                    goLiveLocal();
                } else {
                    sendAction({ action: 'go_live_node' });
                }
            }
            return;
        }

        pendingLiveFocusRef.current = false;
        const boundedIndex = Math.max(0, index);
        if (!pendingLiveFocusRef.current) {
            setHistoryFocusIndex(boundedIndex);
        }
        selectedHistoryIndexRef.current = boundedIndex;
        applyHistoryEntryState(boundedIndex);
        setPreviewPerspectivePlayer(null);
        if (isLocalGame) {
            const snapshot = localSnapshotsRef.current[boundedIndex];
            if (snapshot) {
                engineRef.current.loadSnapshot(snapshot);
                setGameState(prev => ({
                    ...prev,
                    board: snapshot.board.map(row => [...row]),
                    currentPlayer: snapshot.currentPlayer,
                    winner: snapshot.winner,
                    phutballPos: [...snapshot.phutballPos],
                    historyIndex: boundedIndex,
                }));
            }
        } else {
            sendAction({ action: 'view_history', index: boundedIndex });
        }
    };

    // Handler for tree view's "Live" entry - goes to live node with next player perspective
    const handleGoLive = useCallback((nextPlayer) => {
        setHistoryBranchMode(false);
        cancelHistoryHoverPreview();
        setHistoryPreviewState(null);
        const entryCount = (gameState.history ?? []).length;
        pendingLiveFocusRef.current = true;
        enteringTurnModeRef.current = true;  // Don't clear perspective in response handler
        setHistoryFocusIndex(entryCount);
        selectedHistoryIndexRef.current = null;
        pendingTreeSelectionRef.current = null;
        clearStageOnNextStateRef.current = true;
        setStagedMove({ type: 'none', path: [] });
        setJumpPreview(emptyJumpPreview);
        setInvalidPlacement(null);
        // Use the next player's perspective (passed from tree view)
        setPreviewPerspectivePlayer(nextPlayer ?? nodeToMove ?? null);
        // Enter turn mode - viewing from next player's perspective
        setIsInTurnMode(true);
        if (isLocalGame) {
            goLiveLocal();
        } else {
            sendAction({ action: 'go_live_node' });
        }
    }, [gameState.history, cancelHistoryHoverPreview, sendAction, nodeToMove, setJumpPreview, setStagedMove, setInvalidPlacement, isLocalGame, goLiveLocal]);

    // Exit turn mode - go back to viewing the HALT move from mover's perspective
    const handleExitTurnMode = useCallback(() => {
        setIsInTurnMode(false);
        // Set perspective to the mover (who made the live node move)
        const liveId = gameState.historyTree?.liveNodeId;
        if (liveId) {
            const liveNode = nodesById.get(liveId);
            if (liveNode) {
                const moverPerspective = getNodePlayer(liveNode);
                setPreviewPerspectivePlayer(moverPerspective ?? null);
            }
        }
    }, [gameState.historyTree?.liveNodeId, nodesById, getNodePlayer]);

    const handlePreviewHistoryEntry = useCallback((index, active) => {
        if (!Array.isArray(gameState.history)) return;
        if (treePreviewActive || isReplayingHistory) return;

        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }

        if (!active) {
            hoverTimeoutRef.current = setTimeout(() => {
                cancelHistoryHoverPreview();
                setPreviewPerspectivePlayer(null);
                const hasHistorySelection = typeof selectedHistoryIndexRef.current === 'number';
                const restoreIndex = hasHistorySelection
                    ? selectedHistoryIndexRef.current
                    : (!isAtLiveNode && typeof gameState.historyIndex === 'number' ? gameState.historyIndex : null);
                
                if (restoreIndex != null) {
                    applyHistoryEntryState(restoreIndex);
                } else {
                    setStagedMove({ type: 'none', path: [] });
                    setJumpPreview(emptyJumpPreview);
                    setInvalidPlacement(null);
                    if (isAtLiveNode) {
                        setPreviewPerspectivePlayer(livePerspectivePlayer ?? nodeToMove ?? null);
                    }
                }
                setHistoryPreviewState(null);
            }, 60);
            return;
        }

        const hasHistorySelection = typeof selectedHistoryIndexRef.current === 'number';
        if (
            stagedMove?.type
            && stagedMove.type !== 'none'
            && !hasHistorySelection
            && !historyHoverPreviewRef.current.active
        ) {
            return;
        }

        const entryCount = gameState.history.length;
        if (!entryCount && typeof index !== 'number') return;
        const isLivePreview = typeof index === 'number' && index >= entryCount;
        if (isLivePreview) {
            const latest = latestStateRef.current;
            if (!latest || !Array.isArray(latest.board) || latest.board.length === 0) {
                return;
            }
            const latestLiveNodeId = latest.historyTree?.liveNodeId ?? null;
            const latestLiveNode = latestLiveNodeId && nodesById.has(latestLiveNodeId)
                ? nodesById.get(latestLiveNodeId)
                : null;
            const latestPlayer = typeof latestLiveNode?.statePlayer === 'number'
                ? latestLiveNode.statePlayer
                : (typeof latest.currentPlayer === 'number' ? latest.currentPlayer : livePerspectivePlayer ?? nodeToMove ?? null);
            setHistoryPreviewState({
                source: 'live-hover',
                board: cloneBoard(latest.board),
                phutballPos: Array.isArray(latest.phutballPos) ? [...latest.phutballPos] : null,
                currentPlayer: latestPlayer,
                historyIndex: typeof latest.historyIndex === 'number' ? latest.historyIndex : entryCount,
                nodeId: latestLiveNodeId ?? liveNodeId ?? null,
                parentNodeId: latestLiveNode?.parentId ?? null,
            });
            setStagedMove({ type: 'none', path: [] });
            setJumpPreview(emptyJumpPreview);
            setInvalidPlacement(null);
            historyHoverPreviewRef.current = { active: true, index: entryCount, source: 'live-hover' };
            previewRequestRef.current = { source: 'live-hover', historyIndex: entryCount, parentNodeId: null, nodeId: null };
            setPreviewPerspectivePlayer(latestPlayer ?? livePerspectivePlayer ?? nodeToMove ?? null);
            return;
        }
        if (!entryCount) return;
        const boundedIndex = Math.max(0, Math.min(typeof index === 'number' ? index : 0, entryCount - 1));
        if (historyHoverPreviewRef.current.active && historyHoverPreviewRef.current.index === boundedIndex) {
            return;
        }

        historyHoverPreviewRef.current = { active: true, index: boundedIndex, source: 'linear' };
        previewRequestRef.current = { source: 'linear', historyIndex: boundedIndex, parentNodeId: null, nodeId: null };

        if (isLocalGame) {
            const snapshot = localSnapshotsRef.current[boundedIndex];
            if (snapshot) {
                const entry = gameState.history[boundedIndex];
                const parsed = parseHistoryEntryForPreview(entry, {
                    history: gameState.history,
                    index: boundedIndex,
                });
                let preview = null;
                if (parsed?.type === 'placement' && Array.isArray(parsed.position)) {
                    preview = { type: 'placement', path: [parsed.position] };
                } else if (parsed?.type === 'jump' && Array.isArray(parsed.path)) {
                    preview = { type: 'jump', start: parsed.start, path: parsed.path };
                }
                handleHistoryPreviewMessage({
                    active: true,
                    source: 'linear',
                    historyIndex: boundedIndex,
                    board: snapshot.board.map(row => [...row]),
                    phutballPos: Array.isArray(snapshot.phutballPos) ? [...snapshot.phutballPos] : null,
                    currentPlayer: snapshot.currentPlayer,
                    preview,
                });
            }
        } else {
            sendAction({ action: 'preview_history_entry', index: boundedIndex });
        }
        const entryPlayer = extractEntryPlayer(gameState.history?.[boundedIndex]);
        const perspective = applyHistoryEntryState(boundedIndex);
        const resolvedPerspective = entryPlayer ?? perspective ?? null;
        setPreviewPerspectivePlayer(resolvedPerspective);
    }, [gameState.history, gameState.historyIndex, treePreviewActive, isReplayingHistory, stagedMove?.type, cancelHistoryHoverPreview, sendAction, applyHistoryEntryState, setInvalidPlacement, setJumpPreview, setStagedMove, livePerspectivePlayer, nodeToMove, liveNodeId, liveNode, isLocalGame, handleHistoryPreviewMessage]);


    const handleCommitHalt = () => {
        // Block during AI/opponent turn
        if (llmThinking || isCurrentPlayerAI()) {
            return;
        }
        if (historyPreviewState) {
            return;
        }
        const currentId = gameState.historyTree?.currentNodeId;
        const liveId = gameState.historyTree?.liveNodeId;
        const atLiveNode = currentId != null && liveId != null && currentId === liveId;
        // Must be at live AND in turn mode (for tree view) to make moves
        const canMoveInCurrentView = historyView === 'linear' || isInTurnMode;
        if (awaitingAck || !atLiveNode || !canMoveInCurrentView) {
            return;
        }
        const inferredStart = Array.isArray(stagedMove.startPos)
            ? stagedMove.startPos
            : (Array.isArray(gameState.phutballPos) ? [...gameState.phutballPos] : null);

        const movePayload = {
            type: 'jump',
            path: [],
            startPos: inferredStart,
        };

        selectedHistoryIndexRef.current = null;
        setStagedMove(movePayload);
        setJumpPreview(emptyJumpPreview);
        setInvalidPlacement(null);
        // After move: if at live, enter turn mode for next player
        // If branching (not at live), view the new move (not in turn mode)
        setPreviewPerspectivePlayer(null);
        setIsInTurnMode(isAtLiveNode);

        if (isLocalGame) {
            // processLocalMove clears the staged trace itself once the
            // hop-by-hop animation completes.
            processLocalMove(movePayload);
        } else {
            const snapshot = JSON.parse(JSON.stringify(movePayload));
            pendingMoveRef.current = snapshot;
            setAwaitingAck(true);
            const sent = sendAction({ action: 'commit_move', move: movePayload });
            if (!sent) {
                setAwaitingAck(false);
                pendingMoveRef.current = null;
            }
        }
    };

    useEffect(() => {
        const justStoppedReplaying = wasReplayingRef.current && !isReplayingHistory;
        wasReplayingRef.current = isReplayingHistory;

        if (!justStoppedReplaying) return;

        // We just ended replay: clear any staged previews/trails, but DON'T navigate.
        selectedHistoryIndexRef.current = null;
        pendingMoveRef.current = null;
        pendingTreeSelectionRef.current = null;
        clearStageOnNextStateRef.current = true; // discard any server-driven preview on the first post-replay state

        setStagedMove({ type: 'none', path: [] });
        setJumpPreview(emptyJumpPreview);
        setInvalidPlacement(null);
        setAwaitingAck(false);
        setPreviewPerspectivePlayer(null); // Let normal perspective logic resume

        // Optional nicety: re-center without changing the node.
        if (controlsRef.current && !lockCameraDuringReplay) {
            controlsRef.current.fitAndCenter();
        }
        }, [isReplayingHistory]);


    useEffect(() => {
        if (!gameState.board || gameState.board.length === 0) return;
        const controls = controlsRef.current;
        if (!controls) return;

        // Only auto-fit when NOT replaying, or when camera lock is off
        if (!isReplayingHistory || !lockCameraDuringReplay) {
            controls.fitAndCenter();
        }
    }, [gameState.board, isReplayingHistory, lockCameraDuringReplay]);


    const logPanel = (
        <LogPanel
            logHistory={gameState.logHistory}
            initialBoardText={gameState.initialBoardText}
            llmThinking={llmThinking || azLoading}
            llmProvider={azLoading ? 'AlphaZero' : getCurrentAIConfig().provider}
            llmModel={azLoading ? 'Loading model...' : getCurrentAIConfig().model}
            llmError={llmError}
            playerConfig={{
                p1: config?.p1 ?? 'Human',
                p2: config?.p2 ?? 'Human',
                modelName: config?.modelName,
            }}
            puzzleMetadata={puzzleMetadata}
            puzzleEvalResult={puzzleEvalResult}
            puzzleAttemptMode={config?.puzzleAttemptMode || 'manual'}
        />
    );
    const handleRecenter = useCallback(() => {
        controlsRef.current?.fitAndCenter?.(250);
    }, []);

    // Endless mode shows the same overlay while we're waiting for the
    // queue to populate the next puzzle (initial mount, or between solves
    // if the queue happened to dry up). Otherwise the user briefly sees
    // an empty starting board before the popped puzzle paints in.
    const endlessWaiting = isEndlessMode && (endlessNeedsNext || !puzzleMetadata) && !endlessRunSummary;
    const showLoadingOverlay = puzzleFetching || endlessWaiting;
    const loadingLabel = isEndlessMode
        ? 'Loading next puzzle…'
        : `Generating ${puzzleControls.puzzle_type} puzzle…`;

    const boardPanel = (
        <div className="board-container">
            {showLoadingOverlay && (
                <div className="puzzle-loading-overlay">
                    <div className="puzzle-loading-pill">
                        <span className="puzzle-loading-spinner" />
                        <span className="puzzle-loading-label">
                            {loadingLabel}
                        </span>
                    </div>
                </div>
            )}
            <div className="board-status-bar">
                {isPagerDragging && swipeOffsetPct !== 0 && isStackedLayout && (
                    <div
                        className={`swipe-progress${Math.abs(swipeOffsetPct) >= 30 ? ' swipe-progress--ready' : ''}`}
                        style={{
                            [swipeOffsetPct > 0 ? 'left' : 'right']: 0,
                            width: `${Math.min(Math.abs(swipeOffsetPct) / 30 * 100, 100)}%`,
                        }}
                    />
                )}
                <div className="board-indicator-group" aria-live="polite">
                    <div className="coord-indicator">
                        <div className="coord-indicator__label">Cursor</div>
                        <div className="coord-indicator__value">
                            <span className="coord-indicator__axis">R</span>
                            <span>{cursorIndicator.rowLabel}</span>
                            <span className="coord-indicator__divider">·</span>
                            <span className="coord-indicator__axis">C</span>
                            <span>{cursorIndicator.colLabel}</span>
                        </div>
                    </div>
                    <div className="coord-indicator coord-indicator--hover">
                        <div className="coord-indicator__label">Hover</div>
                        <div className="coord-indicator__value">
                            <span className="coord-indicator__axis">R</span>
                            <span>{hoverIndicator.rowLabel}</span>
                            <span className="coord-indicator__divider">·</span>
                            <span className="coord-indicator__axis">C</span>
                            <span>{hoverIndicator.colLabel}</span>
                        </div>
                    </div>
                </div>
                <button
                    type="button"
                    className="board-toggle"
                    onClick={(e) => { toggleCoordSystem(); e.currentTarget.blur(); }}
                >
                    <CoordinateSystemIcon mode={coordSystem} />
                    <span>{coordSystem === 'matrix' ? 'matrix' : 'endzone relative'}</span>
                </button>
                <button
                    type="button"
                    className="board-toggle"
                    onClick={(e) => { setShowMinimap(v => !v); e.currentTarget.blur(); }}
                    aria-label={showMinimap ? 'Hide minimap' : 'Show minimap'}
                    title={showMinimap ? 'Hide minimap' : 'Show minimap'}
                >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="1" y="1" width="14" height="10" rx="1.5" />
                        <circle cx="8" cy="6" r="2" />
                        {!showMinimap && <line x1="2" y1="12" x2="14" y2="0" strokeWidth="2" stroke="currentColor" />}
                    </svg>
                    <span>{showMinimap ? 'minimap' : 'minimap off'}</span>
                </button>
            </div>
            <GameBoard
              key={
                config?.gameMode === 'puzzle' && puzzleMetadata?.seed != null
                  ? `gameboard-puzzle-${puzzleMetadata.seed}`
                  : 'gameboard-standard'
              }
              board={visibleBoard}
              phutballPos={visiblePhutballPos}
              onCellClick={handleCellClick}
              onHoverCell={handleHoverCell}
              onLeaveBoard={handleLeaveBoard}
              coordSystem={coordSystem}
              viewPerspective={effectivePerspective}
              liveNodeId={liveNodeId}
              currentNodeId={currentNodeId}
              boardSessionToken={
                config?.gameMode === 'puzzle' && puzzleMetadata?.seed != null
                  ? `puzzle-${puzzleMetadata.seed}`
                  : null
              }
              isPreviewingStagedMove={Boolean(historyPreviewState)}
              cursorPos={cursorPos}
              hoverPos={hoverPos}
              stagedMove={stagedMove}
              jumpPreview={jumpPreview}
              invalidPlacement={invalidPlacement}
              onControlsReady={registerBoardControls}
              showSegmentBar={isAtLiveNode || (stagedMove?.type === 'jump' && stagedMove.path?.length)}
              showHistoryIndicator={!renderingLivePreview && !isAtLiveNode && !isReplayingHistory}
              disableBoardCursor={hoverPreviewActive || isReplayingHistory}
              showMinimap={showMinimap}
            />
            <div className="action-bar">
              <button
                  type="button"
                  className="board-recenter-btn"
                  onClick={(e) => { handleRecenter(); e.currentTarget.blur(); }}
                  aria-label="Recenter board"
                  title="Recenter board"
              >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                      <circle cx="8" cy="8" r="3" />
                      <line x1="8" y1="0.5" x2="8" y2="3.5" />
                      <line x1="8" y1="12.5" x2="8" y2="15.5" />
                      <line x1="0.5" y1="8" x2="3.5" y2="8" />
                      <line x1="12.5" y1="8" x2="15.5" y2="8" />
                  </svg>
              </button>
              <button disabled={!canAct || !cursorPos} onClick={(e) => { handlePlace(); e.currentTarget.blur(); }}>Place</button>
              <button disabled={!canAct || !cursorPos} onClick={(e) => { handleJump(); e.currentTarget.blur(); }}>Jump</button>
              <button disabled={!canAct || stagedMove.type === 'none' || stagedMove.path.length === 0} onClick={(e) => { handleConfirm(); e.currentTarget.blur(); }}>Confirm</button>
              <button disabled={!canAct || stagedMove.type === 'none' || stagedMove.path.length === 0} onClick={(e) => { handleUndo(); e.currentTarget.blur(); }}>Undo</button>
            </div>
            {visualizationNodeId && nnData && visualizationMode !== 'off' && (
                <>
                    <HeatmapOverlay
                        mode={visualizationMode}
                        nnData={nnData}
                        boardRows={boardRowCount}
                        boardCols={boardColCount}
                        cellSize={40}
                        boardPadding={40}
                        viewPerspective={effectivePerspective}
                    />
                    <div style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        background: 'rgba(11, 7, 20, 0.85)',
                        border: '1px solid rgba(97, 242, 255, 0.4)',
                        borderRadius: '6px',
                        padding: '6px 10px',
                        fontSize: '0.7rem',
                        color: '#61f2ff',
                        fontWeight: '500',
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        pointerEvents: 'none',
                        zIndex: 20,
                    }}>
                        {visualizationMode === 'placement-policy' && 'Placement Policy'}
                        {visualizationMode === 'jump-policy' && 'Jump Policy'}
                        {visualizationMode.startsWith('saliency-') && `Saliency Layer ${visualizationMode.split('-')[1]}`}
                        {nnData?.policyProbs && visualizationMode === 'jump-policy' && (() => {
                            const haltIndex = 2 * boardRowCount * boardColCount;
                            const haltProb = nnData.policyProbs[haltIndex];
                            if (haltProb != null) {
                                return <span style={{ marginLeft: '8px', opacity: 0.8 }}>Halt: {(haltProb * 100).toFixed(1)}%</span>;
                            }
                            return null;
                        })()}
                    </div>
                </>
            )}
            {/* NN Visualizer button in bottom right corner */}
            {config?.nnVisualization && (
                <div className="nn-viz-button-container">
                    <button
                        type="button"
                        className={`nn-viz-button ${visualizationMode !== 'off' ? 'active' : ''}`}
                        onClick={() => {
                            const nodeId = currentNodeId || gameState.historyTree?.liveNodeId;
                            if (!nodeId) return;
                            
                            if (visualizationNodeId === nodeId && showModeSelectorForNode) {
                                // Close the selector
                                setShowModeSelectorForNode(null);
                            } else if (visualizationNodeId === nodeId) {
                                // Reopen the selector
                                setShowModeSelectorForNode(nodeId);
                            } else {
                                // Activate for this node
                                handleToggleVisualization(nodeId);
                            }
                        }}
                        title="Neural Network Visualization"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="5" r="3"/>
                            <circle cx="5" cy="19" r="3"/>
                            <circle cx="19" cy="19" r="3"/>
                            <line x1="12" y1="8" x2="5" y2="16"/>
                            <line x1="12" y1="8" x2="19" y2="16"/>
                        </svg>
                        <span>NN</span>
                    </button>
                    {showModeSelectorForNode && (
                        <VisualizationModeSelector
                            mode={visualizationMode}
                            onModeChange={handleVisualizationModeChange}
                            onClose={handleCloseVisualizationModeSelector}
                        />
                    )}
                </div>
            )}
        </div>
    );
    const historyPanel = (
        <HistoryPanel
            history={gameState.history}
            currentIndex={gameState.historyIndex}
            onHistoryClick={handleHistoryClick}
            onPreviewHistoryEntry={handlePreviewHistoryEntry}
            viewMode={historyView}
            setViewMode={setHistoryView}
            historyTree={gameState.historyTree}
            onSelectTreeNode={handleSelectTreeNode}
            onPreviewTreeEdge={handlePreviewTreeEdge}
            onReplayStateChange={setIsReplayingHistory}
            keyFocus={keyFocus}
            onRequestFocus={setKeyFocus}
            focusedHistoryIndex={historyFocusIndex}
            registerToggleJumpEntry={registerToggleJumpEntry}
            suppressAutoStick={treePreviewActive}
            treePreviewActive={treePreviewActive}
            previewedTreeEdge={previewedTreeEdge}
            onRequestBranch={handleRequestBranch}
            highlightToken={historyHighlightToken}
            highlightHistoryIndex={gameState.historyIndex}
            highlightNodeId={currentNodeId}
            branchReadyNodeId={historyViewingActive ? currentNodeId : null}
            // Move list is always rendered in absolute matrix coordinates so it
            // matches the LogPanel; the global coordSystem toggle only affects
            // the cursor display + heatmap overlay.
            coordSystem="matrix"
            boardRows={boardRowCount}
            boardCols={boardColCount}
            viewPerspective={effectivePerspective}
            onToggleVisualization={handleToggleVisualization}
            visualizationNodeId={visualizationNodeId}
            nnVisualizationEnabled={config?.nnVisualization}
            nnDataStatus={nnDataStatus}
            onGoLive={handleGoLive}
            isInTurnMode={isInTurnMode}
            onExitTurnMode={handleExitTurnMode}
        />
    );
    const panelContentMap = {
        log: logPanel,
        board: boardPanel,
        history: historyPanel,
    };
    const appClassName = `app-container${isStackedLayout ? ' app-container--stacked' : ''}`;
    const mainClassName = `main-content${isStackedLayout ? ' stacked' : ''}`;
    const pagerClasses = ['panel-pager'];
    if (isPagerDragging) {
        pagerClasses.push('panel-pager--dragging');
    }
    if (forceNoTransition) {
        pagerClasses.push('panel-pager--no-transition');
    }
    const pagerStyle = isStackedLayout
        ? {
            width: `${PANEL_CONFIG.length * 100}%`,
            transform: `translateX(calc(-${activePanelIndex * 100 / PANEL_CONFIG.length}% + ${swipeOffsetPct / PANEL_CONFIG.length}%))`
          }
        : undefined;

    const winner = gameState.winner ?? 0;
    const currentLiveNodeId = gameState.historyTree?.liveNodeId ?? null;
    // Show game over modal if there's a winner AND we haven't dismissed this specific game-ending state
    // This allows the modal to reappear if the user branches and reaches a different game over
    // In puzzle mode every puzzle is a single turn; once the responder
    // commits a move the puzzleEvalResult is set, which forces the modal
    // open even when engine.winner is still 0 (e.g. threat placements).
    // Endless mode never shows the per-puzzle modal; solved puzzles
    // auto-advance, and the EndlessSummaryModal handles end-of-run.
    const showGameOverModal = !isEndlessMode
        && (winner !== 0 || puzzleEvalResult !== null)
        && dismissedGameOverNodeId !== currentLiveNodeId;

    // Fire game_complete telemetry once per game
    useEffect(() => {
        if (!isLocalGame) return;
        if (winner === 0) return;
        if (gameCompleteSentRef.current) return;
        gameCompleteSentRef.current = true;

        const mode = config?.gameMode || 'local_multiplayer';
        const aiProvider = mode === 'vs_computer'
            ? (config?.p1 !== 'Human' ? config?.p1 : config?.p2)
            : null;
        const tokens = gameTokenTotalsRef.current;
        const startedAt = gameStartAtRef.current || Date.now();

        trackEvent('game_complete', {
            mode,
            ai_provider: aiProvider,
            ai_model: config?.modelName || null,
            rows: config?.rows,
            cols: config?.cols,
            time_control: config?.timeControl || null,
            winner,
            turn_count: gameState.history?.length ? gameState.history.length - 1 : 0,
            duration_ms: Date.now() - startedAt,
            input_tokens: tokens?.input || 0,
            output_tokens: tokens?.output || 0,
            reasoning_tokens: tokens?.reasoning || 0,
            cached_tokens: tokens?.cached || 0,
            cost_usd: tokens?.costUsd ? +tokens.costUsd.toFixed(6) : 0,
        });
    }, [isLocalGame, winner, config, gameState.history?.length]);

    // Compute game stats (for both in-progress and finished games)
    const gameStats = useMemo(() => {
        const history = gameState.history ?? [];
        if (history.length <= 1) return null; // No moves yet
        const rows = config?.rows ?? 21;
        const midRow = Math.floor(rows / 2);

        // Track stats per player
        const stats = {
            p1: { jumpSequences: [], totalPiecesJumped: 0, totalJumps: 0 },
            p2: { jumpSequences: [], totalPiecesJumped: 0, totalJumps: 0 },
            possession: { p1Turns: 0, p2Turns: 0 },
        };

        // Track ball position for field possession (starts at center)
        let currentBallRow = midRow;
        let ballHasMoved = false;

        // Parse history entries to extract stats
        for (const entry of history) {
            if (!entry || entry === 'Game Start') continue;

            // Determine player
            const playerMatch = entry.match(/P(\d):/);
            if (!playerMatch) continue;
            const player = parseInt(playerMatch[1], 10);
            const pKey = player === 1 ? 'p1' : 'p2';

            // Check for jump moves
            const isJump = entry.includes('Jump');
            if (isJump) {
                // Count arrows to determine jumps in sequence
                const arrowCount = (entry.match(/->/g) || []).length;
                if (arrowCount > 0) {
                    stats[pKey].jumpSequences.push(arrowCount);
                    stats[pKey].totalJumps += arrowCount;

                    // Estimate pieces jumped (simplified: assume 1 piece per jump segment)
                    stats[pKey].totalPiecesJumped += arrowCount;
                }

                // Update ball position based on final position after jump
                const posMatch = entry.match(/\((-?\d+),\s*(-?\d+)\)/g);
                if (posMatch && posMatch.length > 0) {
                    const lastPos = posMatch[posMatch.length - 1];
                    const rowMatch = lastPos.match(/\((-?\d+)/);
                    if (rowMatch) {
                        currentBallRow = parseInt(rowMatch[1], 10);
                        ballHasMoved = true;
                    }
                }
            }

            // Count this turn toward field possession based on current ball position
            // Only count once the ball has moved from center
            if (ballHasMoved) {
                // P1's endzone is at row 0, P2's endzone is at rows-1
                // Ball in P1's territory (row < midRow) = P2 has field position advantage
                // Ball in P2's territory (row > midRow) = P1 has field position advantage
                if (currentBallRow < midRow) {
                    stats.possession.p2Turns++;
                } else if (currentBallRow > midRow) {
                    stats.possession.p1Turns++;
                }
                // Ball at midRow = neutral, don't count either way
            }
        }

        // Calculate averages
        const p1Seqs = stats.p1.jumpSequences;
        const p2Seqs = stats.p2.jumpSequences;

        const result = {
            p1: {
                jumpSequenceCount: p1Seqs.length,
                avgJumpsPerSeq: p1Seqs.length > 0 ? p1Seqs.reduce((a, b) => a + b, 0) / p1Seqs.length : undefined,
                avgPiecesPerJump: stats.p1.totalJumps > 0 ? stats.p1.totalPiecesJumped / stats.p1.totalJumps : undefined,
            },
            p2: {
                jumpSequenceCount: p2Seqs.length,
                avgJumpsPerSeq: p2Seqs.length > 0 ? p2Seqs.reduce((a, b) => a + b, 0) / p2Seqs.length : undefined,
                avgPiecesPerJump: stats.p2.totalJumps > 0 ? stats.p2.totalPiecesJumped / stats.p2.totalJumps : undefined,
            },
            possession: null,
        };

        // Calculate possession percentage
        const totalPossTurns = stats.possession.p1Turns + stats.possession.p2Turns;
        if (totalPossTurns > 0) {
            result.possession = {
                p1: Math.round((stats.possession.p1Turns / totalPossTurns) * 100),
                p2: Math.round((stats.possession.p2Turns / totalPossTurns) * 100),
            };
        }

        // Include piece tilt from server (calculated from actual board state)
        result.pieceTilt = gameState.pieceTilt ?? null;

        // Include stats history time-series from server
        result.statsHistory = gameState.statsHistory ?? [];

        return result;
    }, [gameState.history, config?.rows, gameState.pieceTilt, gameState.statsHistory]);

    const handleMainMenuFromGameOver = useCallback(() => {
        const liveNodeId = gameState.historyTree?.liveNodeId ?? null;
        setDismissedGameOverNodeId(liveNodeId);
        if (onExit) onExit('RETURN_TO_MENU');
    }, [onExit, gameState.historyTree?.liveNodeId]);

    // Seed the engine and all local-game refs/state from a catalog entry.
    // Shared by New Puzzle (fresh entry) and Retry (same entry, new model).
    const applyPuzzleData = useCallback((data) => {
        lastPuzzleDataRef.current = data;
        // New engine, freshly seeded with the new puzzle. Old
        // engine reference is dropped here, after the fetch
        // resolves, so there is no in-between empty state.
        const engine = new PhutballEngine(configRows, configCols);
        engineRef.current = engine;
        engine.loadSnapshot({
        board: data.board,
        currentPlayer: data.currentPlayer,
        phutballPos: data.phutballPos,
        numTurns: 0,
        winner: 0,
        jumpSeq: [],
        jumpVisitCounts: {},
        });
        const puzzleInitialText = engine.textRender(data.currentPlayer);
        localInitialBoardTextRef.current = puzzleInitialText;
        localHistoryRef.current = ['Game Start'];
        localLogHistoryRef.current = [];
        localSnapshotsRef.current = [engine.getSnapshot()];
        localStatsHistoryRef.current = [];

        const rootNode = createTreeNode(-1, 'Game Start', 1, 1, null, false, false, 0, 0);
        localTreeRootRef.current = rootNode;
        localTreeCurrentRef.current = rootNode;
        localTreeUidRef.current = 0;
        localNodeSnapshotsRef.current = new Map();
        localNodeSnapshotsRef.current.set(rootNode.id, engine.getSnapshot());

        setStagedMove({ type: 'none', path: [] });
        setJumpPreview(emptyJumpPreview);
        setInvalidPlacement(null);
        setPuzzleMetadata(data.puzzle || null);
        setPuzzleEvalResult(null);
        puzzleAttemptCommittedRef.current = false;
        setGameState(buildGameStateFromEngine(engine, ['Game Start'], [], puzzleInitialText));
        setCursorPos(data.phutballPos);
    }, [configRows, configCols]);

    const handleNewGame = useCallback(() => {
        setDismissedGameOverNodeId(null);  // Reset so new game's game over will show
        if (isLocalGame) {
            // Puzzle mode: do NOT reset the engine to empty before the fetch.
            // Doing so caused the GameBoard to animate pieces back from the
            // post-solve position to the empty-board layout (looked like the
            // jump being replayed in reverse) before the loading overlay
            // could cover it. Instead, leave the solved board in place,
            // dismiss the modal, raise the loading overlay, and only swap
            // state once the new puzzle has actually arrived.
            if (config?.gameMode === 'puzzle') {
                if (puzzleFetchInFlightRef.current) return;
                // Abort any in-flight multi-hop animation (its remaining
                // setTimeout ticks will compare the generation and bail).
                animGenRef.current += 1;
                setPuzzleEvalResult(null);
                puzzleAttemptCommittedRef.current = false;
                setPuzzleLLMStats(null);
                gameTokenTotalsRef.current = null;
                puzzleFetchInFlightRef.current = true;
                setPuzzleFetching(true);

                // Static-catalog fetch (see fetchPuzzleFromCatalog) instead
                // of /puzzle?... HTTP. Same response shape, no backend needed.
                fetchPuzzleFromCatalog({
                    puzzle_type: puzzleControls.puzzle_type ?? 'win',
                    num_jumps: puzzleControls.num_jumps,
                    player: puzzleControls.player,
                })
                    .then(data => applyPuzzleData(data))
                    .catch(err => console.error('[Puzzle] new-puzzle fetch failed:', err))
                    .finally(() => {
                        puzzleFetchInFlightRef.current = false;
                        setPuzzleFetching(false);
                    });
                return;
            }

            // Non-puzzle local games: original behavior. The empty-engine
            // flash is fine here because there is no preceding "solved"
            // state with consumed stones to animate back from.
            const engine = new PhutballEngine(configRows, configCols);
            engineRef.current = engine;
            localHistoryRef.current = ['Game Start'];
            localLogHistoryRef.current = [];
            localSnapshotsRef.current = [engine.getSnapshot()];
            localStatsHistoryRef.current = [];
            localInitialBoardTextRef.current = engine.textRender(1);

            const rootNode = createTreeNode(-1, 'Game Start', 1, 1, null, false, false, 0, 0);
            localTreeRootRef.current = rootNode;
            localTreeCurrentRef.current = rootNode;
            localTreeUidRef.current = 0;
            localNodeSnapshotsRef.current = new Map();
            localNodeSnapshotsRef.current.set(rootNode.id, engine.getSnapshot());

            setStagedMove({ type: 'none', path: [] });
            setJumpPreview(emptyJumpPreview);
            setInvalidPlacement(null);
            setGameState(buildGameStateFromEngine(engine, ['Game Start'], [], localInitialBoardTextRef.current));
        } else {
            sendAction({
                action: 'new_game',
                config: {
                    rows: config?.rows ?? 21,
                    cols: config?.cols ?? 15,
                    p1: config?.p1 ?? 'Human',
                    p2: config?.p2 ?? 'Human',
                    modelName: config?.modelName ?? null,
                    gameMode: config?.gameMode ?? 'local_multiplayer',
                    puzzle: config?.puzzle ?? null,
                }
            });
        }
    }, [isLocalGame, configRows, configCols, sendAction, config, setStagedMove, setJumpPreview, setInvalidPlacement, buildGameStateFromEngine, puzzleControls, applyPuzzleData]);

    // Watch-an-LLM puzzles: switch the model used for the NEXT attempt. The
    // key comes from the BYOK store for that provider; a provider without a
    // stored key is left disabled in the modal's dropdown.
    const handleChangePuzzleModel = useCallback((provider, model) => {
        const keys = loadApiKeys();
        const apiKey = keys[provider]
            || (puzzleAIOverride?.provider === provider ? puzzleAIOverride.apiKey : null);
        setPuzzleAIOverride({ provider, model, apiKey });
    }, [puzzleAIOverride]);

    // Reload the same puzzle position for another attempt (typically with a
    // different model chosen in the modal).
    const handleRetryPuzzle = useCallback(() => {
        const data = lastPuzzleDataRef.current;
        if (!data || config?.gameMode !== 'puzzle' || puzzleFetchInFlightRef.current) return;
        setDismissedGameOverNodeId(null);
        animGenRef.current += 1;
        setPuzzleEvalResult(null);
        puzzleAttemptCommittedRef.current = false;
        setPuzzleLLMStats(null);
        gameTokenTotalsRef.current = null;
        applyPuzzleData(JSON.parse(JSON.stringify(data)));
    }, [config?.gameMode, applyPuzzleData]);

    // Watch-an-LLM puzzle panel props, shared by the puzzle-complete modal
    // and the pause menu. null when the user solves puzzles by hand.
    const puzzleLLM = useMemo(() => {
        if (!puzzleAIOverride) return null;
        const stored = loadApiKeys();
        return {
            provider: puzzleAIOverride.provider,
            model: puzzleAIOverride.model,
            stats: puzzleLLMStats,
            providers: LLM_PROVIDERS,
            keys: Object.fromEntries(Object.keys(LLM_PROVIDERS).map(pv =>
                [pv, !!(stored[pv] || (puzzleAIOverride.provider === pv && puzzleAIOverride.apiKey))])),
            onChangeModel: handleChangePuzzleModel,
            onRetry: handleRetryPuzzle,
        };
    }, [puzzleAIOverride, puzzleLLMStats, handleChangePuzzleModel, handleRetryPuzzle]);

    const openGameMenu = useCallback(() => {
        const puzzleJudged = config?.gameMode === 'puzzle'
            && config?.puzzleAttemptMode !== 'endless'
            && (puzzleEvalResult !== null || (gameState.winner ?? 0) !== 0);
        if (puzzleJudged) {
            setIsPauseMenuOpen(false);
            setDismissedGameOverNodeId(null);   // re-show the puzzle-complete modal
            return;
        }
        setIsPauseMenuOpen(true);
    }, [config?.gameMode, config?.puzzleAttemptMode, puzzleEvalResult, gameState.winner]);
    openGameMenuRef.current = openGameMenu;

    const handleGameOverReview = useCallback(() => {
        // Track which node's game over was dismissed, so if user branches to a different
        // game-ending state, the modal will show again
        const liveNodeId = gameState.historyTree?.liveNodeId ?? null;
        setDismissedGameOverNodeId(liveNodeId);
    }, [gameState.historyTree?.liveNodeId]);

    return (
        <div
            className={appClassName}
            ref={isStackedLayout ? swipeContainerRef : undefined}
        >
            <button
                className="game-menu-toggle"
                onClick={(e) => {
                    e.currentTarget.blur();
                    openGameMenu();
                }}
                aria-label="Open menu"
            >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <rect x="2" y="4" width="16" height="2" rx="1" />
                    <rect x="2" y="9" width="16" height="2" rx="1" />
                    <rect x="2" y="14" width="16" height="2" rx="1" />
                </svg>
            </button>

            <GameOverModal
                winner={(() => {
                    if (!showGameOverModal) return 0;
                    if (winner) return winner;
                    if (!puzzleEvalResult) return 0;
                    // The "winning" player in the modal is the side the user
                    // was playing as: the responder for threat, otherwise
                    // puzzleMetadata.player. Solved -> that side; failed ->
                    // the opposing side.
                    const userSide = puzzleMetadata?.responder
                        ?? puzzleMetadata?.player
                        ?? 1;
                    return puzzleEvalResult.solved ? userSide : (3 - userSide);
                })()}
                historyLength={gameState.history?.length ?? 0}
                treeNodeCount={nodesById.size}
                stats={gameStats}
                onReview={handleGameOverReview}
                onNewGame={handleNewGame}
                onMainMenu={onExit ? handleMainMenuFromGameOver : undefined}
                onClose={handleGameOverReview}
                puzzleMode={config?.gameMode === 'puzzle'}
                puzzleLLM={puzzleLLM}
                puzzleSolved={
                    config?.gameMode === 'puzzle'
                        ? (puzzleEvalResult ? puzzleEvalResult.solved
                            : (winner === (puzzleMetadata?.responder
                                          ?? puzzleMetadata?.player ?? 0)))
                        : false
                }
                puzzleLabel={puzzleEvalResult?.label ?? null}
                puzzleControls={puzzleControls}
                onPuzzleControlsChange={setPuzzleControls}
            />

            {endlessRunSummary && (
                <div className="endless-summary-overlay" onClick={(e) => e.stopPropagation()}>
                    <div className="endless-summary-card">
                        <div className="endless-summary-title">
                            {endlessRunSummary.cleanFinish ? 'RUN COMPLETE' : 'RUN OVER'}
                        </div>
                        <div className="endless-summary-headline">
                            {endlessRunSummary.totalSolved} solved
                            <span style={{ opacity: 0.55 }}>
                                {'  ·  '}
                                {(endlessRunSummary.totalMs / 1000).toFixed(1)}s total
                            </span>
                        </div>
                        {endlessRunSummary.failedOn && (
                            <div className="endless-summary-subhead">
                                Missed on a {endlessRunSummary.failedOn} puzzle.
                            </div>
                        )}
                        {endlessRunSummary.highscore && (
                            <div className="endless-summary-subhead">
                                {endlessRunSummary.newBest ? (
                                    <span style={{ color: 'var(--highlight-text, #61f2ff)' }}>
                                        New best!{' '}
                                    </span>
                                ) : null}
                                {endlessRunSummary.cap > 0
                                    ? `Best ${endlessRunSummary.cap}-puzzle time: ${(endlessRunSummary.highscore.bestTotalMs / 1000).toFixed(1)}s`
                                    : `Best endless run: ${endlessRunSummary.highscore.maxSolved} solved`}
                            </div>
                        )}
                        <table className="endless-summary-table">
                            <thead>
                                <tr><th>Type</th><th>Solved</th><th>Avg time</th></tr>
                            </thead>
                            <tbody>
                                {['win', 'threat', 'passthrough', 'denial'].map(t => {
                                    const row = endlessRunSummary.perTypeAvg[t];
                                    if (!row) return null;
                                    return (
                                        <tr key={t}>
                                            <td>{t}</td>
                                            <td>{row.count}</td>
                                            <td>{(row.avgMs / 1000).toFixed(1)}s</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        <div className="endless-summary-actions">
                            <button
                                type="button"
                                className="endless-summary-btn"
                                onClick={() => {
                                    // Restart with the same config: clear summary, reset stats,
                                    // re-arm the queue popper.
                                    endlessStatsRef.current = {
                                        startedAt: Date.now(),
                                        byType: { win: [], threat: [], passthrough: [], denial: [] },
                                        currentPuzzleStartedAt: null,
                                        currentPuzzleType: null,
                                    };
                                    setEndlessRunSummary(null);
                                    setPuzzleEvalResult(null);
                                    puzzleAttemptCommittedRef.current = false;
                                    setEndlessNeedsNext(true);
                                }}
                            >
                                Restart run
                            </button>
                            <button
                                type="button"
                                className="endless-summary-btn"
                                onClick={() => onExit && onExit('RETURN_TO_MENU')}
                            >
                                Reconfigure / menu
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <PauseMenu
                isOpen={isPauseMenuOpen}
                onClose={() => setIsPauseMenuOpen(false)}
                onResume={() => setIsPauseMenuOpen(false)}
                onNewGame={() => {
                    setIsPauseMenuOpen(false);
                    handleNewGame();
                }}
                onMainMenu={onExit ? () => onExit('RETURN_TO_MENU') : undefined}
                historyLength={gameState.history?.length ?? 0}
                treeNodeCount={nodesById.size}
                stats={gameStats}
                puzzleMode={config?.gameMode === 'puzzle'}
                puzzleControls={puzzleControls}
                onPuzzleControlsChange={setPuzzleControls}
                puzzleEvalResult={puzzleEvalResult}
                puzzleLLM={puzzleLLM}
            />

            <Header
              currentPlayer={effectivePerspective}
              showTutorialHint={false}
              showCastQuote={config?.gameMode !== 'puzzle' && (gameState.history?.length ?? 0) <= 1 && !gameState.winner}
              puzzleMetadata={config?.gameMode === 'puzzle' ? puzzleMetadata : null}
              puzzleEvalResult={config?.gameMode === 'puzzle' ? puzzleEvalResult : null}
            />
            {isStackedLayout && (
                <div className="panel-indicator-bar">
                    <div className="panel-indicator-track">
                        {PANEL_CONFIG.map((panel, idx) => (
                            <button
                                key={`${panel.id}-dot`}
                                className={`panel-dot${activePanelIndex === idx ? ' active' : ''}`}
                                onClick={() => setActivePanelIndex(idx)}
                                aria-label={panel.label}
                            />
                        ))}
                    </div>
                </div>
            )}
            <main className={mainClassName}>
                {isStackedLayout ? (
                    <div className={pagerClasses.join(' ')} style={pagerStyle}>
                        {PANEL_CONFIG.map((panel, panelIdx) => {
                            const innerClasses = ['panel-page-inner'];
                            if (panel.id === 'board') {
                                innerClasses.push('panel-page-inner--board');
                            }
                            if (panel.id === 'history') {
                                innerClasses.push('panel-page-inner--history');
                            }
                            return (
                                <section
                                    key={panel.id}
                                    className={`panel-page${panelFlash === panelIdx ? ' panel-page--flash' : ''}`}
                                    aria-label={panel.label}
                                >
                                    <div className={innerClasses.join(' ')}>
                                        {panelContentMap[panel.id]}
                                    </div>
                                </section>
                            );
                        })}
                    </div>
                ) : (
                    <>
                        {logPanel}
                        {boardPanel}
                        {historyPanel}
                    </>
                )}
            </main>
        </div>
    );
};
export default App;